/**
 * Backend API Client
 *
 * Provides typed methods for calling the Next.js API backend.
 * All authentication is handled via httpOnly cookies set by the backend.
 *
 * Architecture: Frontend → API Backend (port 3002) → Supabase
 * No additional API routes needed - direct HTTP calls from client/server components.
 *
 * Naming Conventions:
 * - Frontend uses camelCase (createdAt, userId, etc.)
 * - Backend API uses snake_case (created_at, user_id, etc.)
 * - Axios interceptors handle automatic conversion at HTTP boundary:
 *   - Request: camelCase → snake_case
 *   - Response: snake_case → camelCase
 *
 * Features:
 * - Automatic cookie forwarding for Server Components
 * - Built-in timeout (30s) and error handling
 * - Consistent error handling via BackendApiError
 * - CID-generated TypeScript types for type safety
 * - Automatic case conversion between frontend and backend
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { convertKeysToSnake, convertKeysToCamel, type CamelCaseKeys } from '@/lib/utils/case-converter'

// Import CID-generated types from OpenAPI specifications
import type { components as AuthComponents } from '@/types/generated/auth'
import type { components as OrgComponents } from '@/types/generated/organizations'
import type { components as OrgTagComponents } from '@/types/generated/organization-tags'
import type { components as AppTypeComponents } from '@/types/generated/app-types'
import type { components as WorkspaceComponents } from '@/types/generated/workspaces'
import type { components as ApiKeyComponents } from '@/types/generated/api-keys'
import type { components as ManagementApiKeyComponents } from '@/types/generated/management-api-keys'
import type { components as AnalyticsComponents } from '@/types/generated/analytics'
import type { components as OptimizationComponents } from '@/types/generated/optimization'
import type { components as GatewayConfigComponents } from '@/types/generated/gateway-configs'
import type { components as WorkspaceGuardrailsComponents } from '@/types/generated/workspace-guardrails'
import type { components as WorkspaceProviderComponents } from '@/types/generated/workspace-providers'
import type { components as WorkspaceMemberComponents } from '@/types/generated/workspace-members'
import type { components as AudioFileComponents } from '@/types/generated/audio-files'
import type { components as ModelComponents } from '@/types/generated/models'
import type { components as LabChatComponents } from '@/types/generated/lab-chat'
import type { components as LabImageComponents } from '@/types/generated/lab-image'
import type { components as LabVisionComponents } from '@/types/generated/lab-vision'
import type { components as FileComponents } from '@/types/generated/files'
import type { components as CostCenterComponents } from '@/types/generated/cost-centers'
import type { components as CreditComponents } from '@/types/generated/credits'
import type { components as BillingComponents } from '@/types/generated/billing'
import type { components as WorkspaceBudgetComponents } from '@/types/generated/workspace-budgets'
import type { components as UsageAlertComponents } from '@/types/generated/usage-alerts'
import type { components as UsageExportComponents } from '@/types/generated/usage-exports'

const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:3002'

// ===== Types =====
// All types imported from CID-generated schemas for single source of truth

// ===== Model Discovery Types (from CID-generated schemas) =====
export type ModelProduct = ModelComponents['schemas']['ModelProduct']
export type ProductGroup = ModelComponents['schemas']['ProductGroup']
export type ProviderGroup = ModelComponents['schemas']['ProviderGroup']
export type GroupedModelsResponse = ModelComponents['schemas']['GroupedModelsResponse']

export interface AvailableModel {
  id: string
  name: string
  displayName: string
  provider: string
  capabilities: string[] // e.g., ['text', 'chat', 'vision', 'speech-to-text', 'text-to-speech', 'embeddings']
  contextWindow?: number
  inputTokenLimit?: number
  outputTokenLimit?: number
  created?: number
  product?: ModelProduct // Product family for UI grouping
}

// ===== Auth Types (from CID-generated schemas) =====
export type User = AuthComponents['schemas']['User']
export type Session = AuthComponents['schemas']['Session']
// Extended AuthResponse with onboarding fields for zero-config onboarding
export type AuthResponse = AuthComponents['schemas']['AuthResponse'] & {
  /** Indicates if the user was just auto-provisioned during this signin (zero-config onboarding) */
  autoProvisioned?: boolean
  /** Indicates if the user needs to complete onboarding. Frontend should navigate to /onboarding. */
  needsOnboarding?: boolean
}
export type SessionResponse = AuthComponents['schemas']['SessionResponse']
export type SigninRequest = AuthComponents['schemas']['SigninRequest']
export type SignupRequest = AuthComponents['schemas']['SignupRequest']
export type UpdateUserRequest = AuthComponents['schemas']['UpdateUserRequest']
export type UpdateUserResponse = AuthComponents['schemas']['UpdateUserResponse']
export type MessageResponse = AuthComponents['schemas']['MessageResponse']
export type ResetPasswordRequest = AuthComponents['schemas']['ResetPasswordRequest']

// Signup success response (when email verification is disabled)
export interface SignupSuccessResponse {
  user: User
  requiresEmailConfirmation: boolean
}
export type UpdatePasswordRequest = AuthComponents['schemas']['UpdatePasswordRequest']

// OAuth types
export interface OAuthAuthorizeResponse {
  url: string
  provider: string
}

export interface OAuthCallbackRequest {
  code: string
  provider: string
}

export interface OAuthSetSessionRequest {
  accessToken: string
  refreshToken: string
}

// ===== Organization Types (from CID-generated schemas) =====
export type Organization = OrgComponents['schemas']['Organization']
export type OrganizationMember = OrgComponents['schemas']['OrganizationMember']
export type CreateOrganizationRequest = OrgComponents['schemas']['CreateOrganizationRequest']
export type CreateOrganizationResponse = OrgComponents['schemas']['CreateOrganizationResponse']
export type UpdateOrganizationRequest = OrgComponents['schemas']['UpdateOrganizationRequest']
export type UpdateOrganizationResponse = OrgComponents['schemas']['UpdateOrganizationResponse']
export type ListOrganizationMembersResponse = OrgComponents['schemas']['ListOrganizationMembersResponse']
export type InviteMemberRequest = OrgComponents['schemas']['InviteMemberRequest']
export type InviteMemberResponse = OrgComponents['schemas']['InviteMemberResponse']
export type UpdateMemberRoleRequest = OrgComponents['schemas']['UpdateMemberRoleRequest']
export type UpdateMemberRoleResponse = OrgComponents['schemas']['UpdateMemberRoleResponse']

// ===== Organization Tag Types (from CID-generated schemas) =====
export type OrganizationTagKey = OrgTagComponents['schemas']['OrganizationTagKey']
export type OrganizationTagValue = OrgTagComponents['schemas']['OrganizationTagValue']
export type ListOrganizationTagsResponse = OrgTagComponents['schemas']['ListOrganizationTagsResponse']
export type CreateOrganizationTagKeyRequest = OrgTagComponents['schemas']['CreateOrganizationTagKeyRequest']
export type UpdateOrganizationTagKeyRequest = OrgTagComponents['schemas']['UpdateOrganizationTagKeyRequest']
export type AddOrganizationTagValueRequest = OrgTagComponents['schemas']['AddOrganizationTagValueRequest']
export type UpdateOrganizationTagValueRequest = OrgTagComponents['schemas']['UpdateOrganizationTagValueRequest']
export type ValidateOrganizationTagRequest = OrgTagComponents['schemas']['ValidateOrganizationTagRequest']
export type ValidateOrganizationTagResponse = OrgTagComponents['schemas']['ValidateOrganizationTagResponse']

// FO-13 by-tag spend breakdown (FinOps showback). by-cost-center removed (FO-26).
export interface TagValueSpend {
  value: string
  requests: number
  spendCents: number
}

// FO-13 unit economics + cost-confidence distribution (FinOps showback).
export interface UnitEconomics {
  requests: number
  spendCents: number
  totalTokens: number
  promptTokens: number
  completionTokens: number
  cachedTokens: number
  costPerRequestCents: number
  costPerThousandTokensCents: number
  cachedTokenRatio: number
}

export interface CostConfidenceBucket {
  confidence: string
  requests: number
  spendCents: number
}

// FO-UI20 per-gateway-config spend (Cost Export scope selector / FO-33 display).
export interface ConfigSpend {
  gatewayConfigId: string
  name: string
  requests: number
  spendCents: number
}

// App Types
export type AppType = AppTypeComponents['schemas']['AppType']
export type CreateAppTypeRequest = AppTypeComponents['schemas']['CreateAppTypeRequest']
export type UpdateAppTypeRequest = AppTypeComponents['schemas']['UpdateAppTypeRequest']
export type ListAppTypesResponse = AppTypeComponents['schemas']['ListAppTypesResponse']

// ===== Workspace Types (from CID-generated schemas, converted to camelCase to match interceptors) =====
export type Workspace = CamelCaseKeys<WorkspaceComponents['schemas']['Workspace']>
export type CreateWorkspaceRequest = CamelCaseKeys<WorkspaceComponents['schemas']['CreateWorkspaceRequest']>
export type CreateWorkspaceResponse = CamelCaseKeys<WorkspaceComponents['schemas']['CreateWorkspaceResponse']>
export type GetWorkspaceResponse = CamelCaseKeys<WorkspaceComponents['schemas']['GetWorkspaceResponse']>
export type UpdateWorkspaceRequest = CamelCaseKeys<WorkspaceComponents['schemas']['UpdateWorkspaceRequest']>
export type UpdateWorkspaceResponse = CamelCaseKeys<WorkspaceComponents['schemas']['UpdateWorkspaceResponse']>

// ===== Workspace Member Types (from CID-generated schemas) =====
export type WorkspaceMember = WorkspaceMemberComponents['schemas']['WorkspaceMember']
export type ListWorkspaceMembersResponse = WorkspaceMemberComponents['schemas']['ListWorkspaceMembersResponse']
export type InviteWorkspaceMemberRequest = WorkspaceMemberComponents['schemas']['InviteWorkspaceMemberRequest']
export type InviteWorkspaceMemberResponse = WorkspaceMemberComponents['schemas']['InviteWorkspaceMemberResponse']
export type UpdateWorkspaceMemberRoleRequest = WorkspaceMemberComponents['schemas']['UpdateWorkspaceMemberRoleRequest']
export type UpdateWorkspaceMemberRoleResponse = WorkspaceMemberComponents['schemas']['UpdateWorkspaceMemberRoleResponse']

// ===== Team Types (manually defined until CID generation) =====
export interface Team {
  id: string
  organizationId: string
  workspaceId?: string | null
  costCenterId?: string | null
  name: string
  slug: string
  description?: string | null
  isActive: boolean
  metadata?: Record<string, unknown>
  memberCount?: number
  createdAt: string
  updatedAt: string
}

export interface TeamMember {
  id: string
  teamId: string
  userId: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  email?: string
  fullName?: string | null
  createdAt: string
  updatedAt: string
}

export interface TeamsListResponse {
  teams: Team[]
  total: number
  page: number
  pageSize: number
}

export interface TeamMembersListResponse {
  members: TeamMember[]
}

export interface CreateTeamRequest {
  name: string
  description?: string
  workspaceId?: string
  metadata?: Record<string, unknown>
}

export interface UpdateTeamRequest {
  name?: string
  description?: string
  isActive?: boolean
  metadata?: Record<string, unknown>
}

export interface AddTeamMemberRequest {
  email: string
  role: 'admin' | 'member' | 'viewer'
}

export interface UpdateTeamMemberRoleRequest {
  role: 'owner' | 'admin' | 'member' | 'viewer'
}

// ===== API Key Types (from CID-generated schemas, converted to camelCase to match interceptors) =====
// GatewayConfigKey was consolidated into gateway_configs table (ce2219c)
// This interface represents the mapped shape used by GatewayConfigKeysPage
export interface GatewayConfigKey {
  id: string
  apiKeyId?: string
  workspaceId: string
  name: string
  keyPrefix?: string
  gatewayConfigId: string
  gatewayConfigName: string
  lastUsedAt: string | null
  createdAt: string
  isActive: boolean
}
export type LlmApiKey = CamelCaseKeys<ApiKeyComponents['schemas']['LlmApiKey']>
export type CreateLlmApiKeyRequest = CamelCaseKeys<ApiKeyComponents['schemas']['CreateLlmApiKeyRequest']>
export type CreateLlmApiKeyResponse = CamelCaseKeys<ApiKeyComponents['schemas']['CreateLlmApiKeyResponse']>
export type UpdateLlmApiKeyRequest = CamelCaseKeys<ApiKeyComponents['schemas']['UpdateLlmApiKeyRequest']>
export type UpdateLlmApiKeyResponse = CamelCaseKeys<ApiKeyComponents['schemas']['UpdateLlmApiKeyResponse']>
export type ListLlmApiKeysResponse = CamelCaseKeys<ApiKeyComponents['schemas']['ListLlmApiKeysResponse']>
export type VerifyLlmApiKeyRequest = CamelCaseKeys<ApiKeyComponents['schemas']['VerifyLlmApiKeyRequest']>
export type VerifyLlmApiKeyResponse = CamelCaseKeys<ApiKeyComponents['schemas']['VerifyLlmApiKeyResponse']>
export type RevealLlmApiKeyResponse = CamelCaseKeys<ApiKeyComponents['schemas']['RevealLlmApiKeyResponse']>

// ===== Management API Key Types (from CID-generated schemas, converted to camelCase to match interceptors) =====
export type ManagementApiKey = CamelCaseKeys<ManagementApiKeyComponents['schemas']['ManagementApiKey']>
export type CreateManagementApiKeyRequest = CamelCaseKeys<ManagementApiKeyComponents['schemas']['CreateManagementApiKeyRequest']>
export type CreateManagementApiKeyResponse = CamelCaseKeys<ManagementApiKeyComponents['schemas']['CreateManagementApiKeyResponse']>
export type UpdateManagementApiKeyRequest = CamelCaseKeys<ManagementApiKeyComponents['schemas']['UpdateManagementApiKeyRequest']>
export type UpdateManagementApiKeyResponse = CamelCaseKeys<ManagementApiKeyComponents['schemas']['UpdateManagementApiKeyResponse']>
export type ListManagementApiKeysResponse = CamelCaseKeys<ManagementApiKeyComponents['schemas']['ListManagementApiKeysResponse']>
export type RevealManagementApiKeyResponse = CamelCaseKeys<ManagementApiKeyComponents['schemas']['RevealManagementApiKeyResponse']>

// ===== Provider Configuration Types (from CID-generated schemas, converted to camelCase to match interceptors) =====
export type WorkspaceProvider = CamelCaseKeys<WorkspaceProviderComponents['schemas']['ProviderConfig']>
export type GetWorkspaceProvidersResponse = CamelCaseKeys<WorkspaceProviderComponents['schemas']['GetWorkspaceProvidersResponse']>
export type UpdateProviderRequest = CamelCaseKeys<WorkspaceProviderComponents['schemas']['UpdateProviderRequest']>
export type UpdateProviderResponse = CamelCaseKeys<WorkspaceProviderComponents['schemas']['UpdateProviderResponse']>
export type GetAutoRoutingResponse = CamelCaseKeys<WorkspaceProviderComponents['schemas']['GetAutoRoutingResponse']>
export type UpdateAutoRoutingRequest = CamelCaseKeys<WorkspaceProviderComponents['schemas']['UpdateAutoRoutingRequest']>
export type UpdateAutoRoutingResponse = CamelCaseKeys<WorkspaceProviderComponents['schemas']['UpdateAutoRoutingResponse']>
export type AvailableLlmKey = CamelCaseKeys<WorkspaceProviderComponents['schemas']['AvailableLlmKey']>
export type AvailableLlmKeysResponse = CamelCaseKeys<WorkspaceProviderComponents['schemas']['AvailableLlmKeysResponse']>

// ===== Gateway Config Types (from CID-generated schemas, converted to camelCase to match interceptors) =====
type DeepPartial<T> =
  T extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T extends Record<string, unknown>
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T

export type GatewayConfig = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfig']>
export type CreateGatewayConfigRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['CreateGatewayConfigRequest']>
export type CreateGatewayConfigResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['CreateGatewayConfigResponse']>
export type UpdateGatewayConfigRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['UpdateGatewayConfigRequest']>
export type UpdateGatewayConfigInput = DeepPartial<UpdateGatewayConfigRequest>
export type UpdateGatewayConfigResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['UpdateGatewayConfigResponse']>
export type GatewayConfigResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfigResponse']>
export type GatewayConfigsListResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfigsListResponse']>
export type GuardrailsConfig = CamelCaseKeys<GatewayConfigComponents['schemas']['GuardrailsConfig']>
export type GatewayConfigWithContext = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfigWithContext']>
export type PaginationInfo = CamelCaseKeys<GatewayConfigComponents['schemas']['PaginationInfo']>
export type GatewayConfigsWithContextListResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfigsWithContextListResponse']>
export type GatewayConfigApiKeyResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayConfigApiKeyResponse']>
export type RevealKeyResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['RevealKeyResponse']>
export type ProviderName = GatewayConfigComponents['schemas']['ProviderName']
export type AvailableProvidersResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['AvailableProvidersResponse']>
export type AssignedProvider = CamelCaseKeys<GatewayConfigComponents['schemas']['AssignedProvider']>
export type AssignedProvidersResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['AssignedProvidersResponse']>
export type AssignProviderRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['AssignProviderRequest']>
export type ProvisionPlatformKeyResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['ProvisionPlatformKeyResponse']>
export type RequestPlatformKeyResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['RequestPlatformKeyResponse']>
export type AutoAssignProvidersResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['AutoAssignProvidersResponse']>
export type GatewayLlmKeyAssignment = CamelCaseKeys<GatewayConfigComponents['schemas']['GatewayLlmKeyAssignment']>
export type LlmKeyAssignmentsListResponse = CamelCaseKeys<GatewayConfigComponents['schemas']['LlmKeyAssignmentsListResponse']>
export type AddLlmKeyAssignmentRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['AddLlmKeyAssignmentRequest']>
export type UpdateLlmKeyAssignmentRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['UpdateLlmKeyAssignmentRequest']>
export type ProviderKeyAssignment = CamelCaseKeys<GatewayConfigComponents['schemas']['ProviderKeyAssignment']>
export type PlatformKeyRequest = CamelCaseKeys<GatewayConfigComponents['schemas']['PlatformKeyRequest']>
export type GatewayProviderInput = ProviderName | (string & {})
export type GuardrailViolation = WorkspaceGuardrailsComponents['schemas']['GuardrailViolation']
export type ListGuardrailViolationsResponse = WorkspaceGuardrailsComponents['schemas']['ListGuardrailViolationsResponse']
export type ViolationStats = WorkspaceGuardrailsComponents['schemas']['ViolationStats']

export type CreateGatewayConfigInput =
  Pick<CreateGatewayConfigRequest, 'name'> &
  DeepPartial<Omit<CreateGatewayConfigRequest, 'name'>>

// Platform Provider types (for auto-provisioning)
export interface AvailablePlatformProvider {
  provider: string
  provisioningMethod: 'auto' | 'pool_fallback'
  adminKeyConfigured: boolean
  adminKeyActive: boolean
  poolAvailable?: boolean
  poolKeyCount?: number
}

export interface PlatformProvidersResponse {
  providers: AvailablePlatformProvider[]
  allProviders: AvailablePlatformProvider[]
}

// ===== Onboarding Types =====
export interface OnboardingStatusResponse {
  needsOnboarding: boolean
  currentStep: string | null
  completedAt: string | null
  organizationId: string | null
  workspaceId: string | null
  selectionRequired: boolean
  organizations: OrganizationOption[] | null
  message?: string
}

export interface OrganizationOption {
  id: string
  name: string
  slug: string
  role: string
  hasWorkspace: boolean
  workspaceId: string | null
}

export interface ProvisionedOrganization {
  id: string
  name: string
  slug: string
  ownerId?: string
  createdAt?: string
  updatedAt?: string
}

export interface ProvisionedTeam {
  id: string
  name: string
  organizationId: string
  workspaceId: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ProvisionedWorkspace {
  id: string
  name: string
  organizationId: string
  slug?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ProvisionedGatewayConfig {
  id: string
  /** Gateway Configuration Key (gck_*) - this is the key users use for API access */
  gatewayConfigId: string
  name: string
  workspaceId: string
  description?: string | null
  isDefault?: boolean
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ProviderAssignmentResult {
  provider: string
  /** "assigned" | "already_assigned" | "requested" | "failed" */
  status: string
  /** Whether this provider supports auto-provisioning via admin API key */
  supportsAutoProvision: boolean
  /** "platform" or "byok" */
  keyType?: string
  /** How the key was provisioned: "auto_{provider}", "shared_pool", "gpk_pool", "admin_request" */
  provisionedVia?: string
  /** Platform key request ID (when status is "requested") */
  requestId?: string
  error?: string
}

export interface OnboardingCompleteResponse {
  organization: ProvisionedOrganization
  team?: ProvisionedTeam
  workspace: ProvisionedWorkspace
  gatewayConfig: ProvisionedGatewayConfig
  providerAssignments?: ProviderAssignmentResult[]
  message: string
}

/** @deprecated Use OnboardingCompleteResponse instead */
export type QuickProvisionResponse = OnboardingCompleteResponse

// ===== Error Types (from CID-generated schemas) =====
export type ErrorResponse = AuthComponents['schemas']['ErrorResponse']

// ===== Analytics Types (from CID-generated schemas) =====
export type UsageLog = AnalyticsComponents['schemas']['UsageLog']
export type UsageMetric = AnalyticsComponents['schemas']['UsageMetric']
export type UsageSummary = AnalyticsComponents['schemas']['UsageSummary']
export type GetUsageLogsResponse = AnalyticsComponents['schemas']['GetUsageLogsResponse']
export type GetUsageMetricsResponse = AnalyticsComponents['schemas']['GetUsageMetricsResponse']
export type GetUsageSummaryResponse = AnalyticsComponents['schemas']['GetUsageSummaryResponse']
export type GetOrganizationUsageSummaryResponse = AnalyticsComponents['schemas']['GetOrganizationUsageSummaryResponse']

// ===== Usage Log Content Types (from CID-generated schemas) =====
export type ApiRequestContent = AnalyticsComponents['schemas']['ApiRequestContent']
export type RequestTrace = AnalyticsComponents['schemas']['RequestTrace']
export type UsageLogWithContent = AnalyticsComponents['schemas']['UsageLogWithContent']

// ===== Usage Alerts Types (from CID-generated schemas) =====
export type UsageAlert = UsageAlertComponents['schemas']['UsageAlert']
export type CreateUsageAlertRequest = UsageAlertComponents['schemas']['CreateUsageAlertRequest']
export type UpdateUsageAlertRequest = UsageAlertComponents['schemas']['UpdateUsageAlertRequest']
export type UsageAlertHistory = UsageAlertComponents['schemas']['UsageAlertHistory']
export type AlertsListResponse = UsageAlertComponents['schemas']['AlertsListResponse']
export type AlertHistoryResponse = UsageAlertComponents['schemas']['AlertHistoryResponse']

// ===== Export Job Types (from CID-generated schemas) =====
export type ExportJob = UsageExportComponents['schemas']['ExportJob']
export type CreateExportRequest = UsageExportComponents['schemas']['CreateExportRequest']
export type ExportJobsListResponse = UsageExportComponents['schemas']['ExportJobsListResponse']
export type ExportDownloadUrlResponse = UsageExportComponents['schemas']['ExportDownloadUrlResponse']

// ===== Workspace Budget Types (from CID-generated schemas) =====
export type WorkspaceBudget = WorkspaceBudgetComponents['schemas']['WorkspaceBudget']
export type CreateBudgetRequest = WorkspaceBudgetComponents['schemas']['CreateBudgetRequest']
export type UpdateBudgetRequest = WorkspaceBudgetComponents['schemas']['UpdateBudgetRequest']
export type BudgetAlert = WorkspaceBudgetComponents['schemas']['BudgetAlert']
export type BudgetSummary = WorkspaceBudgetComponents['schemas']['BudgetSummary']
export type BudgetAlertsResponse = WorkspaceBudgetComponents['schemas']['BudgetAlertsResponse']

// ===== Provider Costs Types (from CID-generated schemas) =====
export type ProviderCostResult = AnalyticsComponents['schemas']['ProviderCostResult']
export type ProviderCostBucket = AnalyticsComponents['schemas']['ProviderCostBucket']
export type ProviderCostsSummary = AnalyticsComponents['schemas']['ProviderCostsSummary']
export type ProviderCostsResponse = AnalyticsComponents['schemas']['ProviderCostsResponse']

// ===== Optimization Types (from CID-generated schemas) =====
export type OptimizationConfigResponse = OptimizationComponents['schemas']['OptimizationConfigResponse']
export type UpdateOptimizationConfigRequest = OptimizationComponents['schemas']['UpdateOptimizationConfigRequest']
export type OptimizationMetricsResponse = OptimizationComponents['schemas']['OptimizationMetricsResponse']

// ===== Audio File Types (from CID-generated schemas) =====
export type AudioFile = AudioFileComponents['schemas']['AudioFile']
export type ListAudioFilesResponse = AudioFileComponents['schemas']['ListAudioFilesResponse']
export type AudioFileUrlResponse = AudioFileComponents['schemas']['AudioFileUrlResponse']
export type TranscribeRequest = AudioFileComponents['schemas']['TranscribeRequest']
export type TranscribeResponse = AudioFileComponents['schemas']['TranscribeResponse']
export type TranscriptionResult = AudioFileComponents['schemas']['TranscriptionResult']
export type AudioFileErrorResponse = AudioFileComponents['schemas']['ErrorResponse']

// Legacy aliases for backward compatibility
export type TranscriptionResponse = TranscribeResponse

// ===== Multimodal File Types (from CID-generated schemas) =====
export type MultimodalFile = FileComponents['schemas']['MultimodalFile']
export type FileUrlResponse = FileComponents['schemas']['FileUrlResponse']

// ===== Lab Chat Types (from CID-generated schemas) =====
export type LabConversation = LabChatComponents['schemas']['LabConversation']
export type LabConversationSummary = LabChatComponents['schemas']['LabConversationSummary']
export type LabChatParameters = LabChatComponents['schemas']['LabChatParameters']
export type LabChatMessage = LabChatComponents['schemas']['LabChatMessage']
export type LabMessageAttachment = LabChatComponents['schemas']['LabMessageAttachment']
export type LabMessageMetrics = LabChatComponents['schemas']['LabMessageMetrics']
export type ModelConversation = LabChatComponents['schemas']['ModelConversation']
export type CreateLabConversationRequest = LabChatComponents['schemas']['CreateLabConversationRequest']
export type UpdateLabConversationRequest = LabChatComponents['schemas']['UpdateLabConversationRequest']
export type ListLabConversationsResponse = LabChatComponents['schemas']['ListLabConversationsResponse']
export type LabChatCompletionRequest = LabChatComponents['schemas']['LabChatCompletionRequest']

// ===== Lab Vision Types (from CID-generated schemas) =====
export type LabVisionParameters = LabVisionComponents['schemas']['VisionParameters']
export type LabVisionMessage = LabVisionComponents['schemas']['LabChatMessage']
export type LabVisionModelConversation = LabVisionComponents['schemas']['ModelConversation']
export type LabVisionConversation = LabVisionComponents['schemas']['VisionConversation']
export type CreateLabVisionConversationRequest = LabVisionComponents['schemas']['CreateVisionConversationRequest']
export type UpdateLabVisionConversationRequest = LabVisionComponents['schemas']['UpdateVisionConversationRequest']
export type ListLabVisionConversationsResponse = LabVisionComponents['schemas']['ListVisionConversationsResponse']
export type LabVisionCompletionRequest = LabVisionComponents['schemas']['VisionCompletionRequest']

// ===== Lab Image Types (from CID-generated schemas) =====
export type ImageSessionSettings = LabImageComponents['schemas']['ImageSessionSettings']
export type GeneratedImage = LabImageComponents['schemas']['GeneratedImage']
export type ImageModelResult = LabImageComponents['schemas']['ImageModelResult']
export type ImageSessionTurn = LabImageComponents['schemas']['LabImageSessionTurn']
export type LabImageSession = LabImageComponents['schemas']['ImageSession']
export type CreateImageSessionRequest = LabImageComponents['schemas']['CreateImageSessionRequest']
export type UpdateImageSessionRequest = LabImageComponents['schemas']['UpdateImageSessionRequest']
export type AddImageSessionTurnRequest = LabImageComponents['schemas']['AddImageSessionTurnRequest']
export type AddImageSessionTurnResponse = LabImageComponents['schemas']['ImageTurnResponse']
export type ListImageSessionsResponse = LabImageComponents['schemas']['ListImageSessionsResponse']

// ===== Cost Center Types (from CID-generated schemas) =====
export type CostCenter = CostCenterComponents['schemas']['CostCenter']
export type CostCenterMember = CostCenterComponents['schemas']['CostCenterMember']
export type CostCenterUsage = CostCenterComponents['schemas']['CostCenterUsage']
export type CostCenterUsageNode = CostCenterComponents['schemas']['CostCenterUsageNode']
export type ListCostCentersResponse = CostCenterComponents['schemas']['ListCostCentersResponse']
export type CreateCostCenterRequest = CostCenterComponents['schemas']['CreateCostCenterRequest']
export type UpdateCostCenterRequest = CostCenterComponents['schemas']['UpdateCostCenterRequest']
export type ListCostCenterMembersResponse = CostCenterComponents['schemas']['ListCostCenterMembersResponse']
export type AddCostCenterMemberRequest = CostCenterComponents['schemas']['AddCostCenterMemberRequest']
export type UpdateCostCenterMemberRequest = CostCenterComponents['schemas']['UpdateCostCenterMemberRequest']
export type CostCenterUsageResponse = CostCenterComponents['schemas']['CostCenterUsageResponse']
export type CostCenterUsageSummaryResponse = CostCenterComponents['schemas']['CostCenterUsageSummaryResponse']
export type CostCenterUsageTreeResponse = CostCenterComponents['schemas']['CostCenterUsageTreeResponse']
export type CostAllocationExport = CostCenterComponents['schemas']['CostAllocationExport']

// ===== Credit Types (from CID-generated schemas) =====
export type CreditAccount = CreditComponents['schemas']['CreditAccount']
export type CreditBalanceResponse = CreditComponents['schemas']['CreditBalanceResponse']
export type CreditAccountResponse = CreditComponents['schemas']['CreditAccountResponse']
export type CreditTransaction = CreditComponents['schemas']['CreditTransaction']
export type CreditTransactionListResponse = CreditComponents['schemas']['CreditTransactionListResponse']
export type TransactionType = CreditComponents['schemas']['TransactionType']
export type ReferenceType = CreditComponents['schemas']['ReferenceType']
export type FeatureEntitlement = CreditComponents['schemas']['FeatureEntitlement']
export type FeatureEntitlementListResponse = CreditComponents['schemas']['FeatureEntitlementListResponse']
export type FeatureCode = CreditComponents['schemas']['FeatureCode']
export type EntitlementSource = CreditComponents['schemas']['EntitlementSource']
export type FeatureStatusResponse = CreditComponents['schemas']['FeatureStatusResponse']
export type FeatureDisableResponse = CreditComponents['schemas']['FeatureDisableResponse']
export type UsageMeter = CreditComponents['schemas']['UsageMeter']
export type MeterType = CreditComponents['schemas']['MeterType']
export type UsageSummaryResponseCredits = CreditComponents['schemas']['UsageSummaryResponse']
export type UsageBreakdownResponse = CreditComponents['schemas']['UsageBreakdownResponse']
export type CostCenterCredits = CreditComponents['schemas']['CostCenterCredits']
export type CostCenterCreditsResponse = CreditComponents['schemas']['CostCenterCreditsResponse']
export type AllocateCreditsRequest = CreditComponents['schemas']['AllocateCreditsRequest']
export type AllocateCreditsResponse = CreditComponents['schemas']['AllocateCreditsResponse']
export type DeallocateCreditsRequest = CreditComponents['schemas']['DeallocateCreditsRequest']
export type DeallocateCreditsResponse = CreditComponents['schemas']['DeallocateCreditsResponse']

// ===== Billing Types (from CID-generated schemas) =====
export type BillingSummary = BillingComponents['schemas']['BillingSummary']
export type ApplicationBreakdownItem = BillingComponents['schemas']['ApplicationBreakdownItem']
export type CostCenterBreakdownItem = BillingComponents['schemas']['CostCenterBreakdownItem']
export type ProjectBreakdownItem = BillingComponents['schemas']['ProjectBreakdownItem']
export type BillingBreakdown = BillingComponents['schemas']['BillingBreakdown']
export type BillingTrends = BillingComponents['schemas']['BillingTrends']
export type BillingExport = BillingComponents['schemas']['BillingExport']

// ===== Type Guards =====

/**
 * Type guard to check if response is AuthResponse (201 - immediate session)
 */
export function isAuthResponse(data: AuthResponse | MessageResponse): data is AuthResponse {
  return 'accessToken' in data && 'user' in data
}

/**
 * Type guard to check if response is MessageResponse (202 - verification required)
 */
export function isMessageResponse(data: AuthResponse | MessageResponse): data is MessageResponse {
  return 'message' in data && !('accessToken' in data)
}

/**
 * Type guard to check if response is SignupSuccessResponse (201 - immediate session with cookies)
 */
export function isSignupSuccessResponse(data: SignupSuccessResponse | MessageResponse): data is SignupSuccessResponse {
  return 'user' in data && 'requiresEmailConfirmation' in data
}

// ===== Error Handling =====

class BackendApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message)
    this.name = 'BackendApiError'
  }
}

// ===== Axios Instance =====

/**
 * Configured Axios instance for Backend API
 * - Base URL: BACKEND_API_URL
 * - Timeout: 30 seconds
 * - Credentials: Included for cookie handling
 * - Interceptors: Request/response case conversion + cookie forwarding
 *
 * Exported for advanced use cases where access to full response (including status) is needed.
 */
export const axiosInstance = axios.create({
  baseURL: BACKEND_API_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // Send httpOnly cookies from browser
  headers: {
    'Content-Type': 'application/json',
  },
})

// ===== Request Interceptor =====

/**
 * Request interceptor:
 * - Cookies are sent automatically via withCredentials: true in browser
 * - Converts request body from camelCase to snake_case for backend API
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Convert request body from camelCase to snake_case
    if (config.data && typeof config.data === 'object') {
      config.data = convertKeysToSnake(config.data)
    }
    // Convert query params from camelCase to snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = convertKeysToSnake(config.params)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ===== Response Interceptor =====

/**
 * Response interceptor:
 * - Converts response body from snake_case to camelCase for frontend use
 * - Handle errors and convert to BackendApiError
 * - Redirect to sign-in on 401 (session expired)
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Convert response body from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeysToCamel(response.data)
    }
    return response
  },
  (error: AxiosError) => {
    // Handle errors and convert to BackendApiError
    if (error.response) {
      const status = error.response.status
      // Convert error response data from snake_case to camelCase
      const rawData: unknown = error.response.data
      const data = rawData && typeof rawData === 'object' ? convertKeysToCamel(rawData) : rawData

      // Handle 401 Unauthorized - session expired
      if (status === 401) {
        // Only redirect in browser environment
        if (typeof window !== 'undefined') {
          // Store the current URL to redirect back after login
          const currentPath = window.location.pathname + window.location.search
          if (currentPath !== '/signin' && currentPath !== '/signup') {
            // Redirect to sign-in page
            window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`
          }
        }
      }

      // Phase 1 RBAC §1.2: handle 403 Forbidden by surfacing the global
      // access-denied overlay. This makes role-restricted endpoints render
      // a clear UI instead of leaking a generic error to the page. The
      // backend remains the security boundary; this is purely UX.
      if (status === 403 && typeof window !== 'undefined') {
        // Lazy-import to avoid a circular module graph between the API
        // client and the React store / overlay.
        void import('@/lib/stores/useAccessDenied').then(({ useAccessDenied }) => {
          const message =
            data?.message ||
            data?.error?.message ||
            (typeof data?.error === 'string' ? data.error : null) ||
            undefined
          useAccessDenied.getState().show(message)
        })
      }

      // Server responded with error status
      throw new BackendApiError(
        data?.message || data?.error?.message || (typeof data?.error === 'string' ? data.error : null) || `HTTP ${status}`,
        status,
        data
      )
    } else if (error.request) {
      // Request made but no response (network error)
      throw new BackendApiError(
        'Network error: No response from server',
        0,
        { originalError: error.message }
      )
    } else {
      // Something else happened
      throw new BackendApiError(
        error.message || 'Unknown error',
        0,
        { originalError: error }
      )
    }
  }
)

// ===== API Client =====

/**
 * Backend API client with typed methods for all endpoints
 *
 * Cookies are automatically handled:
 * - Browser automatically sends cookies via withCredentials: true
 *
 * Case conversion happens automatically via interceptors:
 * - Request data: camelCase → snake_case
 * - Response data: snake_case → camelCase
 *
 * Usage:
 * ```ts
 * import { backendApi } from '@/lib/api/backend'
 *
 * // Works in both Server and Client Components
 * const orgs = await backendApi.organizations.list()
 * const session = await backendApi.auth.getSession()
 * const newOrg = await backendApi.organizations.create({ name: 'My Org' })
 * ```
 */
export const backendApi = {
  // ===== Auth =====
  auth: {
    /**
     * Sign in a user
     * POST /v1/auth/signin
     */
    async signin(data: SigninRequest): Promise<AuthResponse> {
      const response = await axiosInstance.post('/v1/auth/signin', data)
      return response.data
    },

    /**
     * Sign up a new user
     * POST /v1/auth/signup
     *
     * Returns:
     * - SignupSuccessResponse (201) if email verification is disabled (immediate session with cookies)
     * - MessageResponse (202) if email verification is required
     */
    async signup(data: SignupRequest): Promise<SignupSuccessResponse | MessageResponse> {
      const response = await axiosInstance.post('/v1/auth/signup', data)
      return response.data
    },

    /**
     * Get current session
     * GET /v1/auth/session
     */
    async getSession(): Promise<SessionResponse> {
      const response = await axiosInstance.get('/v1/auth/session')
      return response.data
    },

    /**
     * Update user metadata
     * PATCH /v1/auth/user
     */
    async updateUser(data: UpdateUserRequest): Promise<UpdateUserResponse> {
      const response = await axiosInstance.patch('/v1/auth/user', data)
      return response.data
    },

    /**
     * Sign out current user
     * POST /v1/auth/signout
     */
    async signout(): Promise<MessageResponse> {
      const response = await axiosInstance.post('/v1/auth/signout')
      return response.data
    },

    /**
     * Resend verification email
     * POST /v1/auth/resend-verification
     */
    async resendVerification(email: string): Promise<MessageResponse> {
      const response = await axiosInstance.post('/v1/auth/resend-verification', { email })
      return response.data
    },

    /**
     * Request password reset email
     * POST /v1/auth/reset-password
     */
    async requestPasswordReset(email: string, redirectTo?: string): Promise<MessageResponse> {
      const response = await axiosInstance.post('/v1/auth/reset-password', { email, redirectTo })
      return response.data
    },

    /**
     * Update password with reset token
     * PUT /v1/auth/update-password
     */
    async updatePassword(password: string, accessToken: string): Promise<MessageResponse> {
      const response = await axiosInstance.put('/v1/auth/update-password', {
        password,
        accessToken,
      })
      return response.data
    },

    /**
     * Get OAuth authorization URL
     * GET /v1/auth/oauth/authorize
     *
     * Returns the OAuth authorization URL for the specified provider.
     * The frontend should redirect the user to this URL to initiate the OAuth flow.
     */
    async getOAuthAuthorizeUrl(provider: string, redirectTo?: string): Promise<OAuthAuthorizeResponse> {
      const params = new URLSearchParams({ provider })
      if (redirectTo) {
        params.append('redirectTo', redirectTo)
      }
      const response = await axiosInstance.get(`/v1/auth/oauth/authorize?${params}`)
      return response.data
    },

    /**
     * Exchange OAuth code for session
     * POST /v1/auth/oauth/callback
     *
     * Exchanges the OAuth authorization code for a session.
     * This endpoint validates the code, gets user info from the provider,
     * creates or updates the user profile, and returns session tokens.
     */
    async exchangeOAuthCode(data: OAuthCallbackRequest): Promise<AuthResponse> {
      const response = await axiosInstance.post('/v1/auth/oauth/callback', data)
      return response.data
    },

    /**
     * Set OAuth session from implicit flow tokens
     * POST /v1/auth/oauth/set-session
     *
     * This endpoint is used when the OAuth flow returns tokens directly in the URL hash
     * (implicit flow) rather than an authorization code (PKCE flow).
     * It validates the tokens with Supabase and sets httpOnly cookies for the session.
     */
    async setOAuthSession(data: OAuthSetSessionRequest): Promise<AuthResponse> {
      const response = await axiosInstance.post('/v1/auth/oauth/set-session', data)
      return response.data
    },
  },

  // ===== Onboarding =====
  onboarding: {
    /**
     * Complete onboarding - provisions all resources with optional input
     * POST /v1/onboarding/complete
     *
     * All fields are optional. Provisions organization, workspace, gateway config,
     * cost center, and auto-assigns all supported providers.
     * Derives org name from user's email if not provided.
     */
    async complete(input?: {
      organizationName?: string
      useCase?: string
      keySetup?: string
      byokKeys?: Array<{ provider: string; apiKey: string; name?: string }>
      gatewayConfigName?: string
    }): Promise<OnboardingCompleteResponse> {
      const response = await axiosInstance.post('/v1/onboarding/complete', input || {})
      return response.data
    },

    /** @deprecated Use complete() instead */
    async quickProvision(input?: { organizationName?: string }): Promise<OnboardingCompleteResponse> {
      return this.complete(input)
    },

    /**
     * Get onboarding status
     * GET /v1/onboarding/status
     */
    async getStatus(): Promise<OnboardingStatusResponse> {
      const response = await axiosInstance.get('/v1/onboarding/status')
      return response.data
    },
  },

  // ===== Organizations =====
  organizations: {
    /**
     * Create a new organization
     * POST /v1/organizations
     */
    async create(data: CreateOrganizationRequest): Promise<CreateOrganizationResponse> {
      const response = await axiosInstance.post('/v1/organizations', data)
      return response.data
    },

    /**
     * List all organizations for current user
     * GET /v1/organizations
     */
    async list(): Promise<Organization[]> {
      const response = await axiosInstance.get('/v1/organizations')
      // Backend returns { organizations: [...] }, extract the array
      return response.data.organizations || []
    },

    /**
     * Get organization by ID
     * GET /v1/organizations/:id
     */
    async get(id: string): Promise<Organization> {
      const response = await axiosInstance.get(`/v1/organizations/${id}`)
      return response.data
    },

    /**
     * Update organization
     * PATCH /v1/organizations/:id
     */
    async update(id: string, data: UpdateOrganizationRequest): Promise<UpdateOrganizationResponse> {
      const response = await axiosInstance.patch(`/v1/organizations/${id}`, data)
      return response.data
    },

    /**
     * Delete organization
     * DELETE /v1/organizations/:id
     */
    async delete(id: string): Promise<void> {
      await axiosInstance.delete(`/v1/organizations/${id}`)
    },

    // Organization Members
    members: {
      /**
       * List organization members
       * GET /v1/organizations/:id/members
       */
      async list(organizationId: string): Promise<ListOrganizationMembersResponse> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/members`)
        return response.data
      },

      /**
       * Invite member to organization
       * POST /v1/organizations/:id/members
       */
      async invite(organizationId: string, data: InviteMemberRequest): Promise<InviteMemberResponse> {
        const response = await axiosInstance.post(`/v1/organizations/${organizationId}/members`, data)
        return response.data
      },

      /**
       * Update member role
       * PATCH /v1/organizations/:orgId/members/:memberId
       */
      async updateRole(
        organizationId: string,
        memberId: string,
        data: UpdateMemberRoleRequest
      ): Promise<UpdateMemberRoleResponse> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/members/${memberId}`,
          data
        )
        return response.data
      },

      /**
       * Remove member from organization
       * DELETE /v1/organizations/:orgId/members/:memberId
       */
      async remove(organizationId: string, memberId: string): Promise<void> {
        await axiosInstance.delete(`/v1/organizations/${organizationId}/members/${memberId}`)
      },
    },

    // Data Retention Settings
    retentionSettings: {
      /**
       * Get data retention settings for an organization
       * GET /v1/organizations/:id/settings/data-retention
       */
      async get(organizationId: string): Promise<{ dataRetentionMode: string; providerZdrEnabled: boolean; retentionPeriodDays: number | null }> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/settings/data-retention`)
        return response.data
      },

      /**
       * Update data retention settings for an organization
       * PATCH /v1/organizations/:id/settings/data-retention
       */
      async update(
        organizationId: string,
        data: { dataRetentionMode?: string; providerZdrEnabled?: boolean; retentionPeriodDays?: number | null }
      ): Promise<{ dataRetentionMode: string; providerZdrEnabled: boolean; retentionPeriodDays: number | null }> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/settings/data-retention`,
          data
        )
        return response.data
      },
    },

    // Organization Tags
    tags: {
      /**
       * List organization tags
       * GET /v1/organizations/:id/tags
       */
      async list(organizationId: string): Promise<ListOrganizationTagsResponse> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/tags`)
        return response.data
      },

      /**
       * Create organization tag key
       * POST /v1/organizations/:id/tags
       */
      async create(
        organizationId: string,
        data: CreateOrganizationTagKeyRequest
      ): Promise<OrganizationTagKey> {
        const response = await axiosInstance.post(`/v1/organizations/${organizationId}/tags`, data)
        return response.data
      },

      /**
       * Get organization tag key
       * GET /v1/organizations/:id/tags/:tagKeyId
       */
      async get(organizationId: string, tagKeyId: string): Promise<OrganizationTagKey> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/tags/${tagKeyId}`)
        return response.data
      },

      /**
       * Update organization tag key
       * PATCH /v1/organizations/:id/tags/:tagKeyId
       */
      async update(
        organizationId: string,
        tagKeyId: string,
        data: UpdateOrganizationTagKeyRequest
      ): Promise<OrganizationTagKey> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/tags/${tagKeyId}`,
          data
        )
        return response.data
      },

      /**
       * Delete organization tag key
       * DELETE /v1/organizations/:id/tags/:tagKeyId
       */
      async delete(organizationId: string, tagKeyId: string): Promise<void> {
        await axiosInstance.delete(`/v1/organizations/${organizationId}/tags/${tagKeyId}`)
      },

      /**
       * Add value to tag key
       * POST /v1/organizations/:id/tags/:tagKeyId/values
       */
      async addValue(
        organizationId: string,
        tagKeyId: string,
        data: AddOrganizationTagValueRequest
      ): Promise<OrganizationTagValue> {
        const response = await axiosInstance.post(
          `/v1/organizations/${organizationId}/tags/${tagKeyId}/values`,
          data
        )
        return response.data
      },

      /**
       * Remove value from tag key
       * DELETE /v1/organizations/:id/tags/:tagKeyId/values/:valueId
       */
      async removeValue(
        organizationId: string,
        tagKeyId: string,
        valueId: string
      ): Promise<void> {
        await axiosInstance.delete(
          `/v1/organizations/${organizationId}/tags/${tagKeyId}/values/${valueId}`
        )
      },

      /**
       * Update tag value (e.g., cost center assignment)
       * PATCH /v1/organizations/:id/tags/:tagKeyId/values/:valueId
       */
      async updateValue(
        organizationId: string,
        tagKeyId: string,
        valueId: string,
        data: UpdateOrganizationTagValueRequest
      ): Promise<OrganizationTagValue> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/tags/${tagKeyId}/values/${valueId}`,
          data
        )
        return response.data
      },

      /**
       * Validate tag key and value format
       * POST /v1/organizations/:id/tags/validate
       */
      async validate(
        organizationId: string,
        data: ValidateOrganizationTagRequest
      ): Promise<ValidateOrganizationTagResponse> {
        const response = await axiosInstance.post(
          `/v1/organizations/${organizationId}/tags/validate`,
          data
        )
        return response.data
      },
    },

    // Organization App Types
    appTypes: {
      /**
       * List app types for an organization
       * GET /v1/organizations/:id/app-types
       */
      async list(organizationId: string): Promise<ListAppTypesResponse> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/app-types`)
        return response.data
      },

      /**
       * Create a custom app type
       * POST /v1/organizations/:id/app-types
       */
      async create(organizationId: string, data: CreateAppTypeRequest): Promise<AppType> {
        const response = await axiosInstance.post(`/v1/organizations/${organizationId}/app-types`, data)
        return response.data
      },

      /**
       * Update a custom app type
       * PATCH /v1/organizations/:id/app-types/:appTypeId
       */
      async update(organizationId: string, appTypeId: string, data: UpdateAppTypeRequest): Promise<AppType> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/app-types/${appTypeId}`,
          data
        )
        return response.data
      },

      /**
       * Delete a custom app type
       * DELETE /v1/organizations/:id/app-types/:appTypeId
       */
      async delete(organizationId: string, appTypeId: string): Promise<void> {
        await axiosInstance.delete(`/v1/organizations/${organizationId}/app-types/${appTypeId}`)
      },
    },

    // Organization Cost Centers
    costCenters: {
      /**
       * List cost centers
       * GET /v1/organizations/:id/cost-centers
       */
      async list(
        organizationId: string,
        params?: {
          view?: 'tree' | 'flat'
          includeInactive?: boolean
          parentId?: string
        }
      ): Promise<ListCostCentersResponse> {
        const queryParams = new URLSearchParams()
        if (params?.view) queryParams.set('view', params.view)
        if (params?.includeInactive) queryParams.set('includeInactive', 'true')
        if (params?.parentId) queryParams.set('parentId', params.parentId)

        const url = `/v1/organizations/${organizationId}/cost-centers?${queryParams.toString()}`
        const response = await axiosInstance.get(url)
        return response.data
      },

      /**
       * Create cost center
       * POST /v1/organizations/:id/cost-centers
       */
      async create(
        organizationId: string,
        data: CreateCostCenterRequest
      ): Promise<CostCenter> {
        const response = await axiosInstance.post(
          `/v1/organizations/${organizationId}/cost-centers`,
          data
        )
        return response.data.costCenter
      },

      /**
       * Get cost center
       * GET /v1/organizations/:id/cost-centers/:costCenterId
       */
      async get(
        organizationId: string,
        costCenterId: string,
        params?: { includeChildren?: boolean }
      ): Promise<CostCenter> {
        const queryParams = params?.includeChildren ? '?includeChildren=true' : ''
        const response = await axiosInstance.get(
          `/v1/organizations/${organizationId}/cost-centers/${costCenterId}${queryParams}`
        )
        return response.data.costCenter
      },

      /**
       * Update cost center
       * PATCH /v1/organizations/:id/cost-centers/:costCenterId
       */
      async update(
        organizationId: string,
        costCenterId: string,
        data: UpdateCostCenterRequest
      ): Promise<CostCenter> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/cost-centers/${costCenterId}`,
          data
        )
        return response.data.costCenter
      },

      /**
       * Delete cost center
       * DELETE /v1/organizations/:id/cost-centers/:costCenterId
       */
      async delete(
        organizationId: string,
        costCenterId: string,
        params?: { reassignTo?: string }
      ): Promise<void> {
        const queryParams = params?.reassignTo ? `?reassignTo=${params.reassignTo}` : ''
        await axiosInstance.delete(
          `/v1/organizations/${organizationId}/cost-centers/${costCenterId}${queryParams}`
        )
      },

      // Members
      members: {
        /**
         * List cost center members
         * GET /v1/organizations/:id/cost-centers/:costCenterId/members
         */
        async list(
          organizationId: string,
          costCenterId: string,
          params?: {
            role?: 'owner' | 'admin' | 'member' | 'viewer'
            limit?: number
            offset?: number
          }
        ): Promise<ListCostCenterMembersResponse> {
          const queryParams = new URLSearchParams()
          if (params?.role) queryParams.set('role', params.role)
          if (params?.limit) queryParams.set('limit', params.limit.toString())
          if (params?.offset) queryParams.set('offset', params.offset.toString())

          const url = `/v1/organizations/${organizationId}/cost-centers/${costCenterId}/members?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Add member to cost center
         * POST /v1/organizations/:id/cost-centers/:costCenterId/members
         */
        async add(
          organizationId: string,
          costCenterId: string,
          data: AddCostCenterMemberRequest
        ): Promise<CostCenterMember> {
          const response = await axiosInstance.post(
            `/v1/organizations/${organizationId}/cost-centers/${costCenterId}/members`,
            data
          )
          return response.data
        },

        /**
         * Update member
         * PATCH /v1/organizations/:id/cost-centers/:costCenterId/members/:userId
         */
        async update(
          organizationId: string,
          costCenterId: string,
          userId: string,
          data: UpdateCostCenterMemberRequest
        ): Promise<CostCenterMember> {
          const response = await axiosInstance.patch(
            `/v1/organizations/${organizationId}/cost-centers/${costCenterId}/members/${userId}`,
            data
          )
          return response.data
        },

        /**
         * Remove member from cost center
         * DELETE /v1/organizations/:id/cost-centers/:costCenterId/members/:userId
         */
        async remove(
          organizationId: string,
          costCenterId: string,
          userId: string
        ): Promise<void> {
          await axiosInstance.delete(
            `/v1/organizations/${organizationId}/cost-centers/${costCenterId}/members/${userId}`
          )
        },
      },

      // Usage
      usage: {
        /**
         * Get cost center usage
         * GET /v1/organizations/:id/cost-centers/:costCenterId/usage
         */
        async get(
          organizationId: string,
          costCenterId: string,
          params?: {
            startDate?: string
            endDate?: string
            application?: string
            includeChildren?: boolean
          }
        ): Promise<CostCenterUsageResponse> {
          const queryParams = new URLSearchParams()
          if (params?.startDate) queryParams.set('start_date', params.startDate)
          if (params?.endDate) queryParams.set('end_date', params.endDate)
          if (params?.application) queryParams.set('application', params.application)
          if (params?.includeChildren) queryParams.set('includeChildren', 'true')

          const url = `/v1/organizations/${organizationId}/cost-centers/${costCenterId}/usage?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Get usage summary for all cost centers
         * GET /v1/organizations/:id/cost-centers/usage/summary
         */
        async getSummary(
          organizationId: string,
          params?: {
            startDate?: string
            endDate?: string
            application?: string
          }
        ): Promise<CostCenterUsageSummaryResponse> {
          const queryParams = new URLSearchParams()
          if (params?.startDate) queryParams.set('start_date', params.startDate)
          if (params?.endDate) queryParams.set('end_date', params.endDate)
          if (params?.application) queryParams.set('application', params.application)

          const url = `/v1/organizations/${organizationId}/cost-centers/usage/summary?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Get hierarchical usage tree
         * GET /v1/organizations/:id/cost-centers/usage/tree
         */
        async getTree(
          organizationId: string,
          params?: {
            startDate?: string
            endDate?: string
            application?: string
          }
        ): Promise<CostCenterUsageTreeResponse> {
          const queryParams = new URLSearchParams()
          if (params?.startDate) queryParams.set('start_date', params.startDate)
          if (params?.endDate) queryParams.set('end_date', params.endDate)
          if (params?.application) queryParams.set('application', params.application)

          const url = `/v1/organizations/${organizationId}/cost-centers/usage/tree?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Export cost allocation report
         * GET /v1/organizations/:id/cost-centers/export
         */
        async export(
          organizationId: string,
          params: {
            startDate: string
            endDate: string
            format?: 'csv' | 'json'
            application?: string
          }
        ): Promise<CostAllocationExport | string> {
          const queryParams = new URLSearchParams()
          queryParams.set('start_date', params.startDate)
          queryParams.set('end_date', params.endDate)
          if (params.format) queryParams.set('format', params.format)
          if (params.application) queryParams.set('application', params.application)

          const url = `/v1/organizations/${organizationId}/cost-centers/export?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },
      },
    },

    // Teams
    teams: {
      /**
       * List teams
       * GET /v1/organizations/:id/teams
       */
      async list(
        organizationId: string,
        params?: {
          workspaceId?: string
          search?: string
          page?: number
          pageSize?: number
        }
      ): Promise<TeamsListResponse> {
        const queryParams = new URLSearchParams()
        if (params?.workspaceId) queryParams.set('workspaceId', params.workspaceId)
        if (params?.search) queryParams.set('search', params.search)
        if (params?.page) queryParams.set('page', params.page.toString())
        if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString())

        const url = `/v1/organizations/${organizationId}/teams?${queryParams.toString()}`
        const response = await axiosInstance.get(url)
        return response.data
      },

      /**
       * Create team
       * POST /v1/organizations/:id/teams
       */
      async create(
        organizationId: string,
        data: CreateTeamRequest
      ): Promise<Team> {
        const response = await axiosInstance.post(
          `/v1/organizations/${organizationId}/teams`,
          data
        )
        return response.data.team
      },

      /**
       * Get team
       * GET /v1/organizations/:id/teams/:teamId
       */
      async get(organizationId: string, teamId: string): Promise<Team> {
        const response = await axiosInstance.get(
          `/v1/organizations/${organizationId}/teams/${teamId}`
        )
        return response.data.team
      },

      /**
       * Update team
       * PATCH /v1/organizations/:id/teams/:teamId
       */
      async update(
        organizationId: string,
        teamId: string,
        data: UpdateTeamRequest
      ): Promise<Team> {
        const response = await axiosInstance.patch(
          `/v1/organizations/${organizationId}/teams/${teamId}`,
          data
        )
        return response.data.team
      },

      /**
       * Delete team
       * DELETE /v1/organizations/:id/teams/:teamId
       */
      async delete(organizationId: string, teamId: string): Promise<void> {
        await axiosInstance.delete(
          `/v1/organizations/${organizationId}/teams/${teamId}`
        )
      },

      // Team Members
      members: {
        /**
         * List team members
         * GET /v1/organizations/:id/teams/:teamId/members
         */
        async list(organizationId: string, teamId: string): Promise<TeamMembersListResponse> {
          const response = await axiosInstance.get(
            `/v1/organizations/${organizationId}/teams/${teamId}/members`
          )
          return response.data
        },

        /**
         * Add team member
         * POST /v1/organizations/:id/teams/:teamId/members
         */
        async add(
          organizationId: string,
          teamId: string,
          data: AddTeamMemberRequest
        ): Promise<TeamMember> {
          const response = await axiosInstance.post(
            `/v1/organizations/${organizationId}/teams/${teamId}/members`,
            data
          )
          return response.data.member
        },

        /**
         * Update team member role
         * PATCH /v1/organizations/:id/teams/:teamId/members/:userId
         */
        async updateRole(
          organizationId: string,
          teamId: string,
          userId: string,
          data: UpdateTeamMemberRoleRequest
        ): Promise<TeamMember> {
          const response = await axiosInstance.patch(
            `/v1/organizations/${organizationId}/teams/${teamId}/members/${userId}`,
            data
          )
          return response.data.member
        },

        /**
         * Remove team member
         * DELETE /v1/organizations/:id/teams/:teamId/members/:userId
         */
        async remove(
          organizationId: string,
          teamId: string,
          userId: string
        ): Promise<void> {
          await axiosInstance.delete(
            `/v1/organizations/${organizationId}/teams/${teamId}/members/${userId}`
          )
        },
      },
    },
  },

  // ===== Workspaces =====
  workspaces: {
    /**
     * Create a new workspace
     * POST /v1/workspaces
     */
    async create(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
      const response = await axiosInstance.post('/v1/workspaces', data)
      return response.data
    },

    /**
     * List all workspaces for current user
     * GET /v1/workspaces
     */
    async list(params?: { organizationId?: string }): Promise<Workspace[]> {
      const response = await axiosInstance.get('/v1/workspaces', { params })
      // Backend returns { workspaces: [...] }, extract the array
      return response.data.workspaces || []
    },

    /**
     * Get workspace by ID
     * GET /v1/workspaces/:id
     */
    async get(id: string): Promise<GetWorkspaceResponse> {
      const response = await axiosInstance.get(`/v1/workspaces/${id}`)

      return response.data
    },

    /**
     * Update workspace
     * PATCH /v1/workspaces/:id
     */
    async update(id: string, data: UpdateWorkspaceRequest): Promise<UpdateWorkspaceResponse> {
      const response = await axiosInstance.patch(`/v1/workspaces/${id}`, data)
      return response.data
    },

    /**
     * Delete workspace
     * DELETE /v1/workspaces/:id
     */
    async delete(id: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${id}`)
    },


    // Provider Configuration
    /**
     * Get available models for a workspace.
     * GET /v1/workspaces/:id/models
     */
    async getAvailableModels(
      workspaceId: string,
      gatewayConfigId?: string,
      _capabilities?: string[]
    ): Promise<AvailableModel[]> {
      const response = await axiosInstance.get<{ models: AvailableModel[] }>(
        `/v1/workspaces/${workspaceId}/models`,
        {
          params: gatewayConfigId ? { gatewayConfigId } : undefined,
        }
      )
      return response.data.models
    },

    providers: {
      /**
       * Get workspace provider configuration
       * GET /v1/workspaces/:id/providers
       *
       * Returns all supported providers with enabled status and BYOK availability
       */
      async get(workspaceId: string): Promise<GetWorkspaceProvidersResponse> {
        const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/providers`)
        return response.data
      },

      /**
       * Update provider configuration
       * PATCH /v1/workspaces/:id/providers
       *
       * Enable or disable a specific provider for the workspace
       */
      async update(workspaceId: string, data: UpdateProviderRequest): Promise<UpdateProviderResponse> {
        const response = await axiosInstance.patch(`/v1/workspaces/${workspaceId}/providers`, data)
        return response.data
      },
    },

    /**
     * Get auto-routing configuration
     * GET /v1/workspaces/:id/auto-routing
     *
     * Returns whether automatic routing across enabled providers is active
     */
    async getAutoRouting(workspaceId: string): Promise<GetAutoRoutingResponse> {
      const response = await axiosInstance.get<GetAutoRoutingResponse>(`/v1/workspaces/${workspaceId}/auto-routing`)
      return response.data
    },

    /**
     * Update auto-routing configuration
     * PATCH /v1/workspaces/:id/auto-routing
     *
     * Enable or disable automatic routing across enabled providers
     */
    async updateAutoRouting(workspaceId: string, data: UpdateAutoRoutingRequest): Promise<UpdateAutoRoutingResponse> {
      const response = await axiosInstance.patch<UpdateAutoRoutingResponse>(`/v1/workspaces/${workspaceId}/auto-routing`, data)
      return response.data
    },

    // Workspace Members
    members: {
      /**
       * List workspace members
       * GET /v1/workspaces/:id/members
       */
      async list(workspaceId: string): Promise<ListWorkspaceMembersResponse> {
        const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/members`)
        return response.data
      },

      /**
       * Invite member to workspace
       * POST /v1/workspaces/:id/members
       */
      async invite(workspaceId: string, data: InviteWorkspaceMemberRequest): Promise<InviteWorkspaceMemberResponse> {
        const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/members`, data)
        return response.data
      },

      /**
       * Update member role
       * PATCH /v1/workspaces/:workspaceId/members/:memberId
       */
      async updateRole(
        workspaceId: string,
        memberId: string,
        data: UpdateWorkspaceMemberRoleRequest
      ): Promise<UpdateWorkspaceMemberRoleResponse> {
        const response = await axiosInstance.patch(
          `/v1/workspaces/${workspaceId}/members/${memberId}`,
          data
        )
        return response.data
      },

      /**
       * Remove member from workspace
       * DELETE /v1/workspaces/:workspaceId/members/:memberId
       */
      async remove(workspaceId: string, memberId: string): Promise<void> {
        await axiosInstance.delete(`/v1/workspaces/${workspaceId}/members/${memberId}`)
      },
    },
  },

  // ===== API Keys =====
  apiKeys: {
    // LLM API Keys
    llm: {
      /**
       * List LLM API keys for organization
       * GET /v1/organizations/:id/llm-api-keys
       */
      async list(organizationId: string): Promise<ListLlmApiKeysResponse> {
        const response = await axiosInstance.get(`/v1/organizations/${organizationId}/llm-api-keys`)
        return response.data
      },

      /**
       * List LLM API keys for a specific workspace
       * GET /v1/workspaces/:id/llm-api-keys
       */
      async listByWorkspace(workspaceId: string): Promise<ListLlmApiKeysResponse> {
        const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/llm-api-keys`)
        return response.data
      },

      /**
       * List available LLM API keys for gateway assignment
       * GET /v1/workspaces/:id/available-llm-keys
       * Returns both workspace BYOK keys and platform keys
       */
      async listAvailableForWorkspace(workspaceId: string): Promise<AvailableLlmKeysResponse> {
        const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/available-llm-keys`)
        return response.data
      },

      /**
       * Create LLM API key
       * POST /v1/organizations/:id/llm-api-keys
       */
      async create(organizationId: string, data: CreateLlmApiKeyRequest): Promise<CreateLlmApiKeyResponse> {
        const response = await axiosInstance.post(`/v1/organizations/${organizationId}/llm-api-keys`, data)
        return response.data
      },

      /**
       * Update LLM API key
       * PATCH /v1/organizations/:orgId/llm-api-keys/:keyId
       */
      async update(organizationId: string, keyId: string, data: UpdateLlmApiKeyRequest): Promise<UpdateLlmApiKeyResponse> {
        const response = await axiosInstance.patch(`/v1/organizations/${organizationId}/llm-api-keys/${keyId}`, data)
        return response.data
      },

      /**
       * Delete LLM API key permanently
       * DELETE /v1/organizations/:orgId/llm-api-keys/:keyId
       */
      async delete(organizationId: string, keyId: string): Promise<void> {
        await axiosInstance.delete(`/v1/organizations/${organizationId}/llm-api-keys/${keyId}`)
      },

      /**
       * Reveal the full plaintext of an LLM API key.
       * POST /v1/organizations/:orgId/llm-api-keys/:keyId/reveal
       *
       * Requires org admin. For AWS keys, returns the bare bearer token (not the
       * JSON envelope); the backend extracts api_key from legacy envelope rows.
       */
      async reveal(organizationId: string, keyId: string): Promise<RevealLlmApiKeyResponse> {
        const response = await axiosInstance.post(
          `/v1/organizations/${organizationId}/llm-api-keys/${keyId}/reveal`,
        )
        return response.data
      },

      /**
       * Verify LLM API key
       * POST /v1/organizations/:id/llm-api-keys/verify
       */
      async verify(organizationId: string, data: VerifyLlmApiKeyRequest): Promise<VerifyLlmApiKeyResponse> {
        try {
          const response = await axiosInstance.post(`/v1/organizations/${organizationId}/llm-api-keys/verify`, data)
          return response.data
        } catch (error: unknown) {
          // When verification fails, the API returns 400 with validation result in response body
          if (axios.isAxiosError(error) && error.response?.status === 400 && error.response.data) {
            return error.response.data
          }
          throw error
        }
      },
    },
  },

  // ===== Management API Keys =====
  managementApiKeys: {
    /**
     * List management API keys for an organization
     * GET /v1/organizations/:id/management-api-keys
     */
    async list(organizationId: string): Promise<ListManagementApiKeysResponse> {
      const response = await axiosInstance.get(`/v1/organizations/${organizationId}/management-api-keys`)
      return response.data
    },

    /**
     * Get a specific management API key
     * GET /v1/organizations/:id/management-api-keys/:keyId
     */
    async get(organizationId: string, keyId: string): Promise<ManagementApiKey> {
      const response = await axiosInstance.get(`/v1/organizations/${organizationId}/management-api-keys/${keyId}`)
      return response.data.managementApiKey
    },

    /**
     * Create a new management API key
     * POST /v1/organizations/:id/management-api-keys
     */
    async create(organizationId: string, data: CreateManagementApiKeyRequest): Promise<CreateManagementApiKeyResponse> {
      const response = await axiosInstance.post(`/v1/organizations/${organizationId}/management-api-keys`, data)
      return response.data
    },

    /**
     * Update a management API key
     * PATCH /v1/organizations/:id/management-api-keys/:keyId
     */
    async update(organizationId: string, keyId: string, data: UpdateManagementApiKeyRequest): Promise<UpdateManagementApiKeyResponse> {
      const response = await axiosInstance.patch(`/v1/organizations/${organizationId}/management-api-keys/${keyId}`, data)
      return response.data
    },

    /**
     * Delete a management API key
     * DELETE /v1/organizations/:id/management-api-keys/:keyId
     */
    async delete(organizationId: string, keyId: string): Promise<void> {
      await axiosInstance.delete(`/v1/organizations/${organizationId}/management-api-keys/${keyId}`)
    },

    /**
     * Reveal the full management API key
     * POST /v1/organizations/:id/management-api-keys/:keyId/reveal
     */
    async reveal(organizationId: string, keyId: string): Promise<RevealManagementApiKeyResponse> {
      const response = await axiosInstance.post(`/v1/organizations/${organizationId}/management-api-keys/${keyId}/reveal`)
      return response.data
    },
  },

  // ===== Workspace Management API Keys =====
  workspaceManagementApiKeys: {
    /**
     * List management API keys for a workspace
     * GET /v1/workspaces/:workspaceId/management-api-keys
     */
    async list(workspaceId: string): Promise<ListManagementApiKeysResponse> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/management-api-keys`)
      return response.data
    },

    /**
     * Get a specific workspace management API key
     * GET /v1/workspaces/:workspaceId/management-api-keys/:keyId
     */
    async get(workspaceId: string, keyId: string): Promise<ManagementApiKey> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/management-api-keys/${keyId}`)
      return response.data.managementApiKey
    },

    /**
     * Create a new workspace management API key
     * POST /v1/workspaces/:workspaceId/management-api-keys
     */
    async create(workspaceId: string, data: CreateManagementApiKeyRequest): Promise<CreateManagementApiKeyResponse> {
      const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/management-api-keys`, data)
      return response.data
    },

    /**
     * Update a workspace management API key
     * PATCH /v1/workspaces/:workspaceId/management-api-keys/:keyId
     */
    async update(workspaceId: string, keyId: string, data: UpdateManagementApiKeyRequest): Promise<UpdateManagementApiKeyResponse> {
      const response = await axiosInstance.patch(`/v1/workspaces/${workspaceId}/management-api-keys/${keyId}`, data)
      return response.data
    },

    /**
     * Delete a workspace management API key
     * DELETE /v1/workspaces/:workspaceId/management-api-keys/:keyId
     */
    async delete(workspaceId: string, keyId: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${workspaceId}/management-api-keys/${keyId}`)
    },

    /**
     * Reveal the full workspace management API key
     * POST /v1/workspaces/:workspaceId/management-api-keys/:keyId/reveal
     */
    async reveal(workspaceId: string, keyId: string): Promise<RevealManagementApiKeyResponse> {
      const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/management-api-keys/${keyId}/reveal`)
      return response.data
    },
  },

  // ===== Analytics =====
  analytics: {
    /**
     * Get a gateway request trace by request ID
     * GET /v1/workspaces/:workspaceId/requests/:requestId/trace
     */
    async getRequestTrace(
      workspaceId: string,
      requestId: string,
      options?: { signal?: AbortSignal },
    ): Promise<RequestTrace> {
      const response = await axiosInstance.get(
        `/v1/workspaces/${workspaceId}/requests/${requestId}/trace`,
        { signal: options?.signal },
      )
      return response.data
    },

    /**
     * Get usage logs for a workspace
     * GET /v1/workspaces/:workspaceId/usage/logs
     */
    async getUsageLogs(
      workspaceId: string,
      params?: {
        startDate?: string
        endDate?: string
        provider?: string
        model?: string
        limit?: number
        endUserId?: string
      }
    ): Promise<GetUsageLogsResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      if (params?.provider) queryParams.set('provider', params.provider)
      if (params?.model) queryParams.set('model', params.model)
      if (params?.limit) queryParams.set('limit', params.limit.toString())
      if (params?.endUserId) queryParams.set('end_user_id', params.endUserId)

      const url = `/v1/workspaces/${workspaceId}/usage/logs?${queryParams.toString()}`
      const response = await axiosInstance.get<GetUsageLogsResponse>(url)
      return response.data
    },

    /** Get usage logs across all workspaces the user has access to */
    async getAllUsageLogs(
      params?: {
        startDate?: string
        endDate?: string
        provider?: string
        model?: string
        limit?: number
        endUserId?: string
      }
    ): Promise<GetUsageLogsResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      if (params?.provider) queryParams.set('provider', params.provider)
      if (params?.model) queryParams.set('model', params.model)
      if (params?.limit) queryParams.set('limit', params.limit.toString())
      if (params?.endUserId) queryParams.set('end_user_id', params.endUserId)

      const url = `/v1/usage/logs?${queryParams.toString()}`
      const response = await axiosInstance.get<GetUsageLogsResponse>(url)
      return response.data
    },

    /**
     * Get usage metrics for a workspace
     * GET /v1/workspaces/:workspaceId/usage/metrics
     */
    async getUsageMetrics(
      workspaceId: string,
      params?: {
        startDate?: string
        endDate?: string
        provider?: string
        model?: string
        groupBy?: 'date' | 'provider' | 'model'
      }
    ): Promise<GetUsageMetricsResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      if (params?.provider) queryParams.set('provider', params.provider)
      if (params?.model) queryParams.set('model', params.model)
      if (params?.groupBy) queryParams.set('group_by', params.groupBy)

      const url = `/v1/workspaces/${workspaceId}/usage/metrics?${queryParams.toString()}`
      const response = await axiosInstance.get<GetUsageMetricsResponse>(url)
      return response.data
    },

    /**
     * Get usage summary for a workspace
     * GET /v1/workspaces/:workspaceId/usage/summary
     */
    async getUsageSummary(
      workspaceId: string,
      params?: {
        startDate?: string
        endDate?: string
      }
    ): Promise<GetUsageSummaryResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)

      const url = `/v1/workspaces/${workspaceId}/usage/summary?${queryParams.toString()}`
      const response = await axiosInstance.get<GetUsageSummaryResponse>(url)
      return response.data
    },

    // getCostByCostCenter removed (FO-26: cost_center retired; use getCostByTag).

    /**
     * Spend grouped by the values of one tag key (FO-13)
     * GET /v1/workspaces/:workspaceId/usage/by-tag?tag_key=...
     */
    async getCostByTag(
      workspaceId: string,
      tagKey: string,
      params?: { startDate?: string; endDate?: string; filters?: string }
    ): Promise<{ byTag: TagValueSpend[] }> {
      const queryParams = new URLSearchParams()
      queryParams.set('tag_key', tagKey)
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      // FO-UI9 faceted filters: `key1:v1,v2;key2:v3` (AND across keys, OR within).
      if (params?.filters) queryParams.set('filters', params.filters)
      const url = `/v1/workspaces/${workspaceId}/usage/by-tag?${queryParams.toString()}`
      const response = await axiosInstance.get<{ byTag: TagValueSpend[] }>(url)
      return response.data
    },

    /**
     * Get spend grouped by date, with optional tag filters (FO-UI12).
     * GET /v1/workspaces/:workspaceId/usage/by-date
     */
    async getCostByDate(
      workspaceId: string,
      params?: { startDate?: string; endDate?: string; filters?: string; splitKey?: string }
    ): Promise<{ byDate: { date: string; value?: string; requests: number; spendCents: number }[] }> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      if (params?.filters) queryParams.set('filters', params.filters)
      // FO-UI13: split each day by a tag key's values (date × tag, stacked view).
      if (params?.splitKey) queryParams.set('split_key', params.splitKey)
      const url = `/v1/workspaces/${workspaceId}/usage/by-date?${queryParams.toString()}`
      const response = await axiosInstance.get<{
        byDate: { date: string; value?: string; requests: number; spendCents: number }[]
      }>(url)
      return response.data
    },

    /**
     * Unit-economics rollup over the period (FO-13).
     * GET /v1/workspaces/:workspaceId/usage/unit-economics
     */
    async getUnitEconomics(
      workspaceId: string,
      params?: { startDate?: string; endDate?: string }
    ): Promise<{ unitEconomics: UnitEconomics }> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      const url = `/v1/workspaces/${workspaceId}/usage/unit-economics?${queryParams.toString()}`
      const response = await axiosInstance.get<{ unitEconomics: UnitEconomics }>(url)
      return response.data
    },

    /**
     * Cost-confidence distribution over the period (FO-13).
     * GET /v1/workspaces/:workspaceId/usage/cost-confidence
     */
    async getCostConfidence(
      workspaceId: string,
      params?: { startDate?: string; endDate?: string }
    ): Promise<{ costConfidence: CostConfidenceBucket[] }> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      const url = `/v1/workspaces/${workspaceId}/usage/cost-confidence?${queryParams.toString()}`
      const response = await axiosInstance.get<{ costConfidence: CostConfidenceBucket[] }>(url)
      return response.data
    },

    /**
     * Per-gateway-config spend breakdown over the period (FO-UI20 export-scope
     * selector / FO-33 display). Only configs with costed usage are returned.
     * GET /v1/workspaces/:workspaceId/usage/config-spend
     */
    async getConfigSpend(
      workspaceId: string,
      params?: { startDate?: string; endDate?: string }
    ): Promise<{ configSpend: ConfigSpend[] }> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      const url = `/v1/workspaces/${workspaceId}/usage/config-spend?${queryParams.toString()}`
      const response = await axiosInstance.get<{ configSpend: ConfigSpend[] }>(url)
      return response.data
    },

    /**
     * Generate a cost-data export payload (FO-30): FOCUS CSV / CSV / JSON.
     * Generation only — returns the raw payload + content type for download.
     * `gatewayConfigId` optionally scopes the export to one config (FO-UI20).
     * GET /v1/workspaces/:workspaceId/usage/export
     */
    async getUsageExport(
      workspaceId: string,
      params?: {
        startDate?: string
        endDate?: string
        format?: 'focus_csv' | 'csv' | 'json'
        focusCompliance?: boolean
        gatewayConfigId?: string
        // FO-UI20 per-column FOCUS options + preview row cap.
        includeEffectiveCost?: boolean
        includeListCost?: boolean
        includeAmortizedCost?: boolean
        limit?: number
      }
    ): Promise<{ data: string; contentType: string }> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)
      if (params?.format) queryParams.set('format', params.format)
      if (params?.focusCompliance !== undefined) {
        queryParams.set('focus_compliance', String(params.focusCompliance))
      }
      if (params?.gatewayConfigId) queryParams.set('gateway_config_id', params.gatewayConfigId)
      if (params?.includeEffectiveCost !== undefined) {
        queryParams.set('include_effective_cost', String(params.includeEffectiveCost))
      }
      if (params?.includeListCost !== undefined) {
        queryParams.set('include_list_cost', String(params.includeListCost))
      }
      if (params?.includeAmortizedCost !== undefined) {
        queryParams.set('include_amortized_cost', String(params.includeAmortizedCost))
      }
      if (params?.limit !== undefined) queryParams.set('limit', String(params.limit))
      const url = `/v1/workspaces/${workspaceId}/usage/export?${queryParams.toString()}`
      // Raw payload (CSV or JSON text), not the camelCase-converted object body.
      const response = await axiosInstance.get<string>(url, { responseType: 'text' })
      return {
        data: response.data,
        contentType: response.headers['content-type'] ?? 'text/csv',
      }
    },

    /**
     * Get organization usage summary
     * GET /v1/organizations/:organizationId/usage/summary
     */
    async getOrganizationUsageSummary(
      organizationId: string,
      params?: {
        startDate?: string
        endDate?: string
      }
    ): Promise<GetOrganizationUsageSummaryResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)

      const url = `/v1/organizations/${organizationId}/usage/summary?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    /**
     * Get provider costs from official APIs (e.g., OpenAI Costs API)
     * GET /v1/organizations/:organizationId/provider-costs
     */
    async getProviderCosts(
      organizationId: string,
      params: {
        apiKeyId?: string  // LLM API key ID (for admin keys)
        provider?: 'openai' | 'anthropic' | 'google' | 'azure' | 'aws'
        startTime: number  // Unix timestamp
        endTime?: number   // Unix timestamp
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        models?: string[]
        groupBy?: 'line_item' | 'project'  // Group results by line_item or LLM project
      }
    ): Promise<ProviderCostsResponse> {
      const queryParams = new URLSearchParams()
      if (params.apiKeyId) queryParams.set('api_key_id', params.apiKeyId)
      if (params.provider) queryParams.set('provider', params.provider)
      queryParams.set('start_time', params.startTime.toString())
      if (params.endTime) queryParams.set('end_time', params.endTime.toString())
      if (params.bucketWidth) queryParams.set('bucket_width', params.bucketWidth)
      if (params.limit) queryParams.set('limit', params.limit.toString())
      if (params.page) queryParams.set('page', params.page)
      if (params.projectIds) queryParams.set('project_ids', params.projectIds.join(','))
      if (params.models) queryParams.set('models', params.models.join(','))
      if (params.groupBy) queryParams.set('group_by', params.groupBy)

      const url = `/v1/organizations/${organizationId}/provider-costs?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    /**
     * Get provider usage for a specific capability (usage metrics only, no cost data)
     * GET /v1/organizations/:organizationId/provider-costs/:capability
     */
    async getProviderUsagesByCapability(
      organizationId: string,
      capability: 'completions' | 'embeddings' | 'moderations' | 'images' | 'audio_transcriptions' | 'audio_speeches' | 'vector_stores' | 'code_interpreter_sessions',
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ): Promise<AnalyticsComponents['schemas']['ProviderCapabilityResponse']> {
      const queryParams = new URLSearchParams()
      if (params.apiKeyId) queryParams.set('api_key_id', params.apiKeyId)
      queryParams.set('start_time', params.startTime.toString())
      if (params.endTime) queryParams.set('end_time', params.endTime.toString())
      if (params.bucketWidth) queryParams.set('bucket_width', params.bucketWidth)
      if (params.limit) queryParams.set('limit', params.limit.toString())
      if (params.page) queryParams.set('page', params.page)
      if (params.projectIds) queryParams.set('project_ids', params.projectIds.join(','))
      if (params.groupBy) queryParams.set('group_by', params.groupBy)

      const url = `/v1/organizations/${organizationId}/provider-costs/${capability}?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    /**
     * Get chat completions costs
     */
    async getCompletionsCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'completions', params)
    },

    /**
     * Get embeddings costs
     */
    async getEmbeddingsCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'embeddings', params)
    },

    /**
     * Get images (DALL-E) costs
     */
    async getImagesCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'images', params)
    },

    /**
     * Get audio transcriptions (Whisper) costs
     */
    async getAudioTranscriptionsCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'audio_transcriptions', params)
    },

    /**
     * Get audio speeches (TTS) costs
     */
    async getAudioSpeechesCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'audio_speeches', params)
    },

    /**
     * Get moderations costs
     */
    async getModerationsCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'moderations', params)
    },

    /**
     * Get vector stores costs
     */
    async getVectorStoresCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'vector_stores', params)
    },

    /**
     * Get code interpreter costs
     */
    async getCodeInterpreterCosts(
      organizationId: string,
      params: {
        apiKeyId?: string
        startTime: number
        endTime?: number
        bucketWidth?: '1m' | '1h' | '1d'
        limit?: number
        page?: string
        projectIds?: string[]
        groupBy?: 'line_item' | 'project'
      }
    ) {
      return this.getProviderUsagesByCapability(organizationId, 'code_interpreter_sessions', params)
    },

    // ===== Log Details =====

    /**
     * Get a single usage log with content
     * GET /v1/workspaces/:workspaceId/usage/logs/:logId
     */
    async getUsageLogDetail(
      workspaceId: string,
      logId: string
    ): Promise<UsageLogWithContent> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/logs/${logId}`)
      return response.data
    },

    /**
     * Get just the content for a usage log
     * GET /v1/workspaces/:workspaceId/usage/logs/:logId/content
     */
    async getUsageLogContent(
      workspaceId: string,
      logId: string
    ): Promise<ApiRequestContent | null> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/logs/${logId}/content`)
      return response.data
    },

    // ===== Alerts =====

    /**
     * List all alerts for a workspace
     * GET /v1/workspaces/:workspaceId/usage/alerts
     */
    async listAlerts(workspaceId: string): Promise<AlertsListResponse> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/alerts`)
      return response.data
    },

    /**
     * Get a single alert
     * GET /v1/workspaces/:workspaceId/usage/alerts/:alertId
     */
    async getAlert(workspaceId: string, alertId: string): Promise<UsageAlert> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/alerts/${alertId}`)
      return response.data
    },

    /**
     * Create a new alert
     * POST /v1/workspaces/:workspaceId/usage/alerts
     */
    async createAlert(
      workspaceId: string,
      request: CreateUsageAlertRequest
    ): Promise<UsageAlert> {
      const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/usage/alerts`, request)
      return response.data
    },

    /**
     * Update an alert
     * PATCH /v1/workspaces/:workspaceId/usage/alerts/:alertId
     */
    async updateAlert(
      workspaceId: string,
      alertId: string,
      request: UpdateUsageAlertRequest
    ): Promise<UsageAlert> {
      const response = await axiosInstance.patch(`/v1/workspaces/${workspaceId}/usage/alerts/${alertId}`, request)
      return response.data
    },

    /**
     * Delete an alert
     * DELETE /v1/workspaces/:workspaceId/usage/alerts/:alertId
     */
    async deleteAlert(workspaceId: string, alertId: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${workspaceId}/usage/alerts/${alertId}`)
    },

    /**
     * Get alert trigger history
     * GET /v1/workspaces/:workspaceId/usage/alerts/history
     */
    async getAlertHistory(
      workspaceId: string,
      params?: { alertId?: string; limit?: number }
    ): Promise<AlertHistoryResponse> {
      const queryParams = new URLSearchParams()
      if (params?.alertId) queryParams.set('alert_id', params.alertId)
      if (params?.limit) queryParams.set('limit', params.limit.toString())
      const url = `/v1/workspaces/${workspaceId}/usage/alerts/history?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    // ===== Exports =====

    /**
     * List export jobs for a workspace
     * GET /v1/workspaces/:workspaceId/usage/exports
     */
    async listExports(
      workspaceId: string,
      params?: { limit?: number }
    ): Promise<ExportJobsListResponse> {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.set('limit', params.limit.toString())
      const url = `/v1/workspaces/${workspaceId}/usage/exports?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    /**
     * Create a new export job
     * POST /v1/workspaces/:workspaceId/usage/exports
     */
    async createExport(
      workspaceId: string,
      request: CreateExportRequest
    ): Promise<ExportJob> {
      const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/usage/exports`, request)
      return response.data
    },

    /**
     * Get export job status
     * GET /v1/workspaces/:workspaceId/usage/exports/:jobId
     */
    async getExport(workspaceId: string, jobId: string): Promise<ExportJob> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/exports/${jobId}`)
      return response.data
    },

    /**
     * Delete an export job
     * DELETE /v1/workspaces/:workspaceId/usage/exports/:jobId
     */
    async deleteExport(workspaceId: string, jobId: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${workspaceId}/usage/exports/${jobId}`)
    },

    /**
     * Get download URL for a completed export
     * GET /v1/workspaces/:workspaceId/usage/exports/:jobId/download
     */
    async getExportDownloadUrl(
      workspaceId: string,
      jobId: string
    ): Promise<ExportDownloadUrlResponse> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/usage/exports/${jobId}/download`)
      return response.data
    },
  },

  // ===== Budgets =====
  budgets: {
    /**
     * Get budget for a workspace
     * GET /v1/workspaces/:workspaceId/budgets
     */
    async getBudget(workspaceId: string): Promise<WorkspaceBudget | null> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/budgets`)
      return response.data
    },

    /**
     * Get budget summary with projections
     * GET /v1/workspaces/:workspaceId/budgets/summary
     */
    async getBudgetSummary(workspaceId: string): Promise<BudgetSummary> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/budgets/summary`)
      return response.data
    },

    /**
     * Create budget for a workspace
     * POST /v1/workspaces/:workspaceId/budgets
     */
    async createBudget(
      workspaceId: string,
      request: CreateBudgetRequest
    ): Promise<WorkspaceBudget> {
      const response = await axiosInstance.post(`/v1/workspaces/${workspaceId}/budgets`, request)
      return response.data
    },

    /**
     * Update budget
     * PATCH /v1/workspaces/:workspaceId/budgets/:budgetId
     */
    async updateBudget(
      workspaceId: string,
      budgetId: string,
      request: UpdateBudgetRequest
    ): Promise<WorkspaceBudget> {
      const response = await axiosInstance.patch(
        `/v1/workspaces/${workspaceId}/budgets/${budgetId}`,
        request
      )
      return response.data
    },

    /**
     * Delete budget
     * DELETE /v1/workspaces/:workspaceId/budgets/:budgetId
     */
    async deleteBudget(workspaceId: string, budgetId: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${workspaceId}/budgets/${budgetId}`)
    },

    /**
     * Get budget alert history
     * GET /v1/workspaces/:workspaceId/budgets/alerts
     */
    async getBudgetAlerts(
      workspaceId: string,
      params?: { limit?: number }
    ): Promise<BudgetAlertsResponse> {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.set('limit', params.limit.toString())

      const url = `/v1/workspaces/${workspaceId}/budgets/alerts?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },
  },

  // ===== Optimization =====
  optimization: {
    /**
     * Get optimization configuration for a workspace
     * GET /v1/workspaces/:workspaceId/optimization/config
     */
    async getConfig(workspaceId: string): Promise<OptimizationConfigResponse> {
      const response = await axiosInstance.get(`/v1/workspaces/${workspaceId}/optimization/config`)
      return response.data
    },

    /**
     * Update optimization configuration for a workspace
     * PUT /v1/workspaces/:workspaceId/optimization/config
     */
    async updateConfig(
      workspaceId: string,
      config: UpdateOptimizationConfigRequest
    ): Promise<OptimizationConfigResponse> {
      const response = await axiosInstance.put(
        `/v1/workspaces/${workspaceId}/optimization/config`,
        config
      )
      return response.data
    },

    /**
     * Get optimization metrics for a workspace
     * GET /v1/workspaces/:workspaceId/optimization/metrics
     */
    async getMetrics(
      workspaceId: string,
      params?: {
        startDate?: string
        endDate?: string
      }
    ): Promise<OptimizationMetricsResponse> {
      const queryParams = new URLSearchParams()
      if (params?.startDate) queryParams.set('start_date', params.startDate)
      if (params?.endDate) queryParams.set('end_date', params.endDate)

      const url = `/v1/workspaces/${workspaceId}/optimization/metrics?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },
  },

  // ===== Gateway Configs =====
  gatewayConfigs: {
    /**
     * List all gateway configurations across all workspaces the user has access to
     * GET /v1/gateway-configs
     */
    async listAll(params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
    }): Promise<GatewayConfigsWithContextListResponse> {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.set('page', params.page.toString())
      if (params?.pageSize) queryParams.set('page_size', params.pageSize.toString())
      if (params?.search) queryParams.set('search', params.search)
      if (params?.isActive !== undefined) queryParams.set('is_active', params.isActive.toString())

      const url = `/v1/gateway-configs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await axiosInstance.get<GatewayConfigsWithContextListResponse>(url)
      return response.data
    },

    /**
     * List gateway configurations for a workspace
     * GET /v1/workspaces/:workspaceId/gateway-configs
     */
    async list(workspaceId: string): Promise<GatewayConfig[]> {
      const response = await axiosInstance.get<GatewayConfigsListResponse>(`/v1/workspaces/${workspaceId}/gateway-configs`)
      return response.data.gatewayConfigs
    },

    /**
     * Get gateway configuration by ID
     * GET /v1/workspaces/:workspaceId/gateway-configs/:configId
     */
    async get(workspaceId: string, configId: string): Promise<GatewayConfig> {
      const response = await axiosInstance.get<GatewayConfigResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}`)
      return response.data.gatewayConfig
    },

    /**
     * Create gateway configuration
     * POST /v1/workspaces/:workspaceId/gateway-configs
     */
    async create(
      workspaceId: string,
      data: CreateGatewayConfigInput
    ): Promise<CreateGatewayConfigResponse> {
      const response = await axiosInstance.post<CreateGatewayConfigResponse>(`/v1/workspaces/${workspaceId}/gateway-configs`, data)
      return response.data
    },

    /**
     * Update gateway configuration
     * PUT /v1/workspaces/:workspaceId/gateway-configs/:configId
     */
    async update(
      workspaceId: string,
      configId: string,
      data: UpdateGatewayConfigInput
    ): Promise<UpdateGatewayConfigResponse> {
      const response = await axiosInstance.put<UpdateGatewayConfigResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}`, data)
      return response.data
    },

    /**
     * Delete gateway configuration
     * DELETE /v1/workspaces/:workspaceId/gateway-configs/:configId
     */
    async delete(workspaceId: string, configId: string): Promise<void> {
      await axiosInstance.delete(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}`)
    },

    /**
     * Get decrypted API key for a gateway configuration (for lab/testing)
     * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/api-key
     */
    async getApiKey(workspaceId: string, configId: string): Promise<GatewayConfigApiKeyResponse> {
      const response = await axiosInstance.get<GatewayConfigApiKeyResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}/api-key`)
      return response.data
    },

    /**
     * Rotate the gck_* key for a gateway configuration
     * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/rotate-key
     */
    async rotateKey(workspaceId: string, configId: string): Promise<GatewayConfigResponse> {
      const response = await axiosInstance.post<GatewayConfigResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}/rotate-key`)
      return response.data
    },

    /**
     * Reveal the full gck_* key for a gateway configuration
     * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/reveal-key
     */
    async revealKey(workspaceId: string, configId: string): Promise<RevealKeyResponse> {
      const response = await axiosInstance.get<RevealKeyResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}/reveal-key`)
      return response.data
    },

    // Provider Assignment
    providers: {
      /**
       * Get available providers for a gateway configuration
       * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/available-providers
       *
       * Returns providers that have at least one unassigned key available
       */
      async getAvailable(
        workspaceId: string,
        configId: string
      ): Promise<AvailableProvidersResponse> {
        const response = await axiosInstance.get<AvailableProvidersResponse>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/available-providers`
        )
        return response.data
      },

      /**
       * Get assigned providers for a gateway configuration
       * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/assigned-providers
       *
       * Returns providers currently assigned to this gateway config
       */
      async getAssigned(
        workspaceId: string,
        configId: string
      ): Promise<AssignedProvidersResponse> {
        const response = await axiosInstance.get<AssignedProvidersResponse>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/assigned-providers`
        )
        return response.data
      },

      /**
       * Assign a provider to a gateway configuration
       * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/providers
       *
       * Auto-selects an available key (BYOK first, then platform)
       */
      async assign(
        workspaceId: string,
        configId: string,
        provider: GatewayProviderInput
      ): Promise<AssignedProvider> {
        const response = await axiosInstance.post<AssignedProvider>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/providers`,
          { provider }
        )
        return response.data
      },

      /**
       * Unassign a provider from a gateway configuration
       * DELETE /v1/workspaces/:workspaceId/gateway-configs/:configId/providers/:provider
       *
       * Frees the key for use by other gateway configurations
       */
      async unassign(
        workspaceId: string,
        configId: string,
        provider: GatewayProviderInput
      ): Promise<void> {
        await axiosInstance.delete(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/providers/${provider}`
        )
      },

      /**
       * Auto-provision a platform API key via the provider's Admin API
       * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/provision-platform-key
       *
       * Creates a new LLM API key via the provider's Admin API and assigns it
       * to this gateway config. Fails if the provider does not support auto-provisioning.
       */
      async provisionPlatformKey(
        workspaceId: string,
        configId: string,
        provider: GatewayProviderInput
      ): Promise<ProvisionPlatformKeyResponse> {
        const response = await axiosInstance.post<ProvisionPlatformKeyResponse>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/provision-platform-key`,
          { provider }
        )
        return response.data
      },

      /**
       * Request the platform admin to provision an LLM API key
       * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/request-platform-key
       *
       * Creates a pending request for the admin to manually provision and assign
       * a provider's LLM API key for this gateway configuration.
       */
      async requestPlatformKey(
        workspaceId: string,
        configId: string,
        provider: GatewayProviderInput
      ): Promise<RequestPlatformKeyResponse> {
        const response = await axiosInstance.post<RequestPlatformKeyResponse>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/request-platform-key`,
          { provider }
        )
        return response.data
      },

      /**
       * Auto-assign default providers to a gateway configuration
       * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/auto-assign-providers
       *
       * Bulk-assigns default providers using the fallback chain
       * (BYOK → shared platform → auto-provision → admin request).
       */
      async autoAssignProviders(
        workspaceId: string,
        configId: string
      ): Promise<AutoAssignProvidersResponse> {
        const response = await axiosInstance.post<AutoAssignProvidersResponse>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/auto-assign-providers`
        )
        return response.data
      },

    },

    // LLM Key Assignments
    llmKeys: {
      /**
       * List LLM key assignments for a gateway configuration
       * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/llm-keys
       */
      async list(workspaceId: string, configId: string): Promise<LlmKeyAssignmentsListResponse> {
        const response = await axiosInstance.get<LlmKeyAssignmentsListResponse>(`/v1/workspaces/${workspaceId}/gateway-configs/${configId}/llm-keys`)
        return response.data
      },

      /**
       * Add LLM key assignment to gateway configuration
       * POST /v1/workspaces/:workspaceId/gateway-configs/:configId/llm-keys
       */
      async add(
        workspaceId: string,
        configId: string,
        data: AddLlmKeyAssignmentRequest
      ): Promise<GatewayLlmKeyAssignment> {
        const response = await axiosInstance.post<GatewayLlmKeyAssignment>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/llm-keys`,
          data
        )
        return response.data
      },

      /**
       * Update LLM key assignment (toggle primary/fallback status)
       * PUT /v1/workspaces/:workspaceId/gateway-configs/:configId/llm-keys/:assignmentId
       */
      async update(
        workspaceId: string,
        configId: string,
        assignmentId: string,
        data: UpdateLlmKeyAssignmentRequest
      ): Promise<GatewayLlmKeyAssignment> {
        const response = await axiosInstance.put<GatewayLlmKeyAssignment>(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/llm-keys/${assignmentId}`,
          data
        )
        return response.data
      },

      /**
       * Remove LLM key assignment from gateway configuration
       * DELETE /v1/workspaces/:workspaceId/gateway-configs/:configId/llm-keys/:assignmentId
       */
      async remove(workspaceId: string, configId: string, assignmentId: string): Promise<void> {
        await axiosInstance.delete(
          `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/llm-keys/${assignmentId}`
        )
      },
    },
  },

  // ===== Audio Files =====
  audio: {
    /**
     * Upload an audio file for transcription
     * POST /v1/audio/upload
     */
    async uploadFile(workspaceId: string, file: File): Promise<AudioFile> {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('workspaceId', workspaceId)

      // Use transformRequest to let axios handle FormData content-type automatically
      const response = await axiosInstance.post('/v1/audio/upload', formData, {
        transformRequest: [(data) => data], // Don't transform, keep as FormData
      })
      return response.data
    },

    /**
     * List audio files for a workspace
     * GET /v1/workspaces/:workspaceId/audio
     */
    async list(
      workspaceId: string,
      params?: {
        status?: 'uploaded' | 'processing' | 'completed' | 'failed'
        limit?: number
        offset?: number
      }
    ): Promise<ListAudioFilesResponse> {
      const queryParams = new URLSearchParams()
      if (params?.status) queryParams.set('status', params.status)
      if (params?.limit) queryParams.set('limit', params.limit.toString())
      if (params?.offset) queryParams.set('offset', params.offset.toString())

      const url = `/v1/workspaces/${workspaceId}/audio?${queryParams.toString()}`
      const response = await axiosInstance.get(url)
      return response.data
    },

    /**
     * Get audio file metadata
     * GET /v1/audio/:audioFileId
     */
    async get(audioFileId: string): Promise<AudioFile> {
      const response = await axiosInstance.get(`/v1/audio/${audioFileId}`)
      return response.data
    },

    /**
     * Delete audio file
     * DELETE /v1/audio/:audioFileId
     */
    async delete(audioFileId: string): Promise<void> {
      await axiosInstance.delete(`/v1/audio/${audioFileId}`)
    },

    /**
     * Get signed URL for audio file
     * GET /v1/audio/:audioFileId/url
     */
    async getUrl(audioFileId: string): Promise<AudioFileUrlResponse> {
      const response = await axiosInstance.get(`/v1/audio/${audioFileId}/url`)
      return response.data
    },

    /**
     * Transcribe audio file with specified models
     * POST /v1/audio/:audioFileId/transcribe
     */
    async transcribe(
      audioFileId: string,
      data: TranscribeRequest
    ): Promise<TranscribeResponse> {
      const response = await axiosInstance.post(`/v1/audio/${audioFileId}/transcribe`, data)
      return response.data
    },
  },

  // ===== Multimodal Files =====
  files: {
    /**
     * Upload a multimodal file (image, audio, video, document)
     * POST /v1/files/upload
     * @param workspaceId - The workspace ID
     * @param file - The file to upload
     * @param options - Optional parameters including conversationId to link file to a conversation
     */
    async upload(
      workspaceId: string,
      file: File,
      options?: { conversationId?: string }
    ): Promise<MultimodalFile> {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('workspaceId', workspaceId)
      if (options?.conversationId) {
        formData.append('conversationId', options.conversationId)
      }

      const response = await axiosInstance.post('/v1/files/upload', formData, {
        transformRequest: [(data) => data], // Don't transform, keep as FormData
      })
      return response.data
    },

    /**
     * Get file metadata
     * GET /v1/files/:fileId
     */
    async get(fileId: string): Promise<MultimodalFile> {
      const response = await axiosInstance.get(`/v1/files/${fileId}`)
      return response.data
    },

    /**
     * Delete file
     * DELETE /v1/files/:fileId
     */
    async delete(fileId: string): Promise<void> {
      await axiosInstance.delete(`/v1/files/${fileId}`)
    },

    /**
     * Get signed URL for file access
     * GET /v1/files/:fileId/url
     */
    async getUrl(fileId: string, expiresIn?: number): Promise<FileUrlResponse> {
      const queryParams = expiresIn ? `?expires_in=${expiresIn}` : ''
      const response = await axiosInstance.get(`/v1/files/${fileId}/url${queryParams}`)
      return response.data
    },
  },

  // ===== Lab Chat =====
  lab: {
    // Conversations
    conversations: {
      /**
       * List lab conversations for a workspace
       * GET /v1/workspaces/:workspaceId/lab/conversations
       */
      async list(
        workspaceId: string,
        params?: { limit?: number; offset?: number }
      ): Promise<ListLabConversationsResponse> {
        const queryParams = new URLSearchParams()
        if (params?.limit) queryParams.set('limit', params.limit.toString())
        if (params?.offset) queryParams.set('offset', params.offset.toString())

        const url = `/v1/workspaces/${workspaceId}/lab/conversations?${queryParams.toString()}`
        const response = await axiosInstance.get(url)
        return response.data
      },

      /**
       * Get a specific conversation
       * GET /v1/workspaces/:workspaceId/lab/conversations/:conversationId
       */
      async get(workspaceId: string, conversationId: string): Promise<LabConversation> {
        const response = await axiosInstance.get(
          `/v1/workspaces/${workspaceId}/lab/conversations/${conversationId}`
        )
        // Backend wraps response in {conversation: ...}
        return response.data.conversation
      },

      /**
       * Create a new conversation
       * POST /v1/workspaces/:workspaceId/lab/conversations
       */
      async create(
        workspaceId: string,
        data: CreateLabConversationRequest
      ): Promise<LabConversation> {
        const response = await axiosInstance.post(
          `/v1/workspaces/${workspaceId}/lab/conversations`,
          data
        )
        // Backend wraps response in {conversation: ...}
        return response.data.conversation
      },

      /**
       * Update a conversation
       * PUT /v1/workspaces/:workspaceId/lab/conversations/:conversationId
       */
      async update(
        workspaceId: string,
        conversationId: string,
        data: UpdateLabConversationRequest
      ): Promise<LabConversation> {
        const response = await axiosInstance.put(
          `/v1/workspaces/${workspaceId}/lab/conversations/${conversationId}`,
          data
        )
        // Backend wraps response in {conversation: ...}
        return response.data.conversation
      },

      /**
       * Delete a conversation
       * DELETE /v1/workspaces/:workspaceId/lab/conversations/:conversationId
       */
      async delete(workspaceId: string, conversationId: string): Promise<void> {
        await axiosInstance.delete(
          `/v1/workspaces/${workspaceId}/lab/conversations/${conversationId}`
        )
      },
    },

    // Chat Completions
    chat: {
      /**
       * Stream a chat completion via Gateway
       * POST /v1/workspaces/:workspaceId/lab/chat/completions
       *
       * Returns a Response object with SSE stream body.
       * Use this with response.body.getReader() for streaming.
       *
       * Works in both Server Components (auto-forwards cookies) and Client Components (browser sends cookies).
       */
      async streamCompletion(
        workspaceId: string,
        data: LabChatCompletionRequest
      ): Promise<Response> {
        // Use native fetch for streaming (axios doesn't support streaming well)
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        }

        // Browser handles cookies via credentials: 'include'
        const response = await fetch(
          `${BACKEND_API_URL}/v1/workspaces/${workspaceId}/lab/chat/completions`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(data),
          }
        )

        if (!response.ok) {
          const errorText = await response.text()
          throw new BackendApiError(
            `Chat completion failed: ${errorText}`,
            response.status,
            { error: errorText }
          )
        }

        return response
      },
    },

    // Vision Lab
    vision: {
      // Conversations
      conversations: {
        /**
         * List lab vision conversations for a workspace
         * GET /v1/workspaces/:workspaceId/lab/vision/conversations
         */
        async list(
          workspaceId: string,
          params?: { limit?: number; offset?: number }
        ): Promise<ListLabVisionConversationsResponse> {
          const queryParams = new URLSearchParams()
          if (params?.limit) queryParams.set('limit', params.limit.toString())
          if (params?.offset) queryParams.set('offset', params.offset.toString())

          const url = `/v1/workspaces/${workspaceId}/lab/vision/conversations?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Get a specific vision conversation
         * GET /v1/workspaces/:workspaceId/lab/vision/conversations/:conversationId
         */
        async get(workspaceId: string, conversationId: string): Promise<LabVisionConversation> {
          const response = await axiosInstance.get(
            `/v1/workspaces/${workspaceId}/lab/vision/conversations/${conversationId}`
          )
          // Backend wraps response in {conversation: ...}
          return response.data.conversation
        },

        /**
         * Create a new vision conversation
         * POST /v1/workspaces/:workspaceId/lab/vision/conversations
         */
        async create(
          workspaceId: string,
          data: CreateLabVisionConversationRequest
        ): Promise<LabVisionConversation> {
          const response = await axiosInstance.post(
            `/v1/workspaces/${workspaceId}/lab/vision/conversations`,
            data
          )
          // Backend wraps response in {conversation: ...}
          return response.data.conversation
        },

        /**
         * Update a vision conversation
         * PUT /v1/workspaces/:workspaceId/lab/vision/conversations/:conversationId
         */
        async update(
          workspaceId: string,
          conversationId: string,
          data: UpdateLabVisionConversationRequest
        ): Promise<LabVisionConversation> {
          const response = await axiosInstance.put(
            `/v1/workspaces/${workspaceId}/lab/vision/conversations/${conversationId}`,
            data
          )
          // Backend wraps response in {conversation: ...}
          return response.data.conversation
        },

        /**
         * Delete a vision conversation
         * DELETE /v1/workspaces/:workspaceId/lab/vision/conversations/:conversationId
         */
        async delete(workspaceId: string, conversationId: string): Promise<void> {
          await axiosInstance.delete(
            `/v1/workspaces/${workspaceId}/lab/vision/conversations/${conversationId}`
          )
        },
      },

      /**
       * Stream a vision completion via Gateway
       * POST /v1/workspaces/:workspaceId/lab/vision/completions
       *
       * Returns a Response object with SSE stream body.
       * Use this with response.body.getReader() for streaming.
       *
       * Works in both Server Components (auto-forwards cookies) and Client Components (browser sends cookies).
       */
      async streamCompletion(
        workspaceId: string,
        data: LabVisionCompletionRequest
      ): Promise<Response> {
        // Use native fetch for streaming (axios doesn't support streaming well)
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        }

        // Browser handles cookies via credentials: 'include'
        const response = await fetch(
          `${BACKEND_API_URL}/v1/workspaces/${workspaceId}/lab/vision/completions`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(data),
          }
        )

        if (!response.ok) {
          const errorText = await response.text()
          throw new BackendApiError(
            `Vision completion failed: ${errorText}`,
            response.status,
            { error: errorText }
          )
        }

        return response
      },

    },

    // Models

    // Image Generation
    image: {


      // ===== Image Sessions (Multi-Model Comparison) =====
      sessions: {
        /**
         * List image sessions
         * GET /v1/workspaces/:workspaceId/lab/image/sessions
         */
        async list(
          workspaceId: string,
          params?: { limit?: number; offset?: number }
        ): Promise<ListImageSessionsResponse> {
          const queryParams = new URLSearchParams()
          if (params?.limit) queryParams.set('limit', params.limit.toString())
          if (params?.offset) queryParams.set('offset', params.offset.toString())

          const url = `/v1/workspaces/${workspaceId}/lab/image/sessions?${queryParams.toString()}`
          const response = await axiosInstance.get(url)
          return response.data
        },

        /**
         * Create a new image session
         * POST /v1/workspaces/:workspaceId/lab/image/sessions
         */
        async create(
          workspaceId: string,
          data: CreateImageSessionRequest
        ): Promise<LabImageSession> {
          const response = await axiosInstance.post(
            `/v1/workspaces/${workspaceId}/lab/image/sessions`,
            data
          )
          // Backend wraps response in {session: ...}
          return response.data.session
        },

        /**
         * Get a specific image session
         * GET /v1/workspaces/:workspaceId/lab/image/sessions/:sessionId
         */
        async get(
          workspaceId: string,
          sessionId: string
        ): Promise<LabImageSession> {
          const response = await axiosInstance.get(
            `/v1/workspaces/${workspaceId}/lab/image/sessions/${sessionId}`
          )
          // Backend wraps response in {session: ...}
          return response.data.session
        },

        /**
         * Update an image session
         * PUT /v1/workspaces/:workspaceId/lab/image/sessions/:sessionId
         */
        async update(
          workspaceId: string,
          sessionId: string,
          data: UpdateImageSessionRequest
        ): Promise<LabImageSession> {
          const response = await axiosInstance.put(
            `/v1/workspaces/${workspaceId}/lab/image/sessions/${sessionId}`,
            data
          )
          // Backend wraps response in {session: ...}
          return response.data.session
        },

        /**
         * Delete an image session
         * DELETE /v1/workspaces/:workspaceId/lab/image/sessions/:sessionId
         */
        async delete(workspaceId: string, sessionId: string): Promise<void> {
          await axiosInstance.delete(
            `/v1/workspaces/${workspaceId}/lab/image/sessions/${sessionId}`
          )
        },

        /**
         * Add a turn to an image session
         * POST /v1/workspaces/:workspaceId/lab/image/sessions/:sessionId/turns
         */
        async addTurn(
          workspaceId: string,
          sessionId: string,
          data: AddImageSessionTurnRequest
        ): Promise<AddImageSessionTurnResponse> {
          const response = await axiosInstance.post(
            `/v1/workspaces/${workspaceId}/lab/image/sessions/${sessionId}/turns`,
            data
          )
          return response.data
        },
      },

    },

},

  // ===== Platform Providers =====
  platformProviders: {
    /**
     * Get available platform providers for provisioning
     * GET /v1/platform-providers
     *
     * Returns providers that have admin keys configured (for auto-provisioning)
     * or pool keys available (for pool fallback providers).
     */
    async getAvailable(): Promise<PlatformProvidersResponse> {
      const response = await axiosInstance.get('/v1/platform-providers')
      return response.data
    },
  },

  // ===== Credits =====
  credits: {
    /**
     * Get credit balance for organization
     * GET /v1/credits/balance?organizationId=<uuid>
     */
    async getBalance(organizationId: string): Promise<CreditBalanceResponse> {
      const response = await axiosInstance.get('/v1/credits/balance', {
        params: { organization_id: organizationId },
      })
      return response.data
    },

    /**
     * Get credit account details
     * GET /v1/credits/account?organization_id=<uuid>
     */
    async getAccount(organizationId: string): Promise<CreditAccountResponse> {
      const response = await axiosInstance.get('/v1/credits/account', {
        params: { organization_id: organizationId },
      })
      return response.data
    },

    /**
     * List credit transactions
     * GET /v1/credits/transactions?organization_id=<uuid>&limit=50&offset=0
     */
    async listTransactions(
      organizationId: string,
      options: {
        limit?: number
        offset?: number
        transactionType?: TransactionType
        startDate?: string
        endDate?: string
        costCenterId?: string
        gatewayConfigId?: string
      } = {}
    ): Promise<CreditTransactionListResponse> {
      const { transactionType, startDate, endDate, costCenterId, gatewayConfigId, ...rest } = options
      const response = await axiosInstance.get('/v1/credits/transactions', {
        params: {
          organization_id: organizationId,
          transaction_type: transactionType,
          start_date: startDate,
          end_date: endDate,
          cost_center_id: costCenterId,
          gateway_config_id: gatewayConfigId,
          ...rest,
        },
      })
      return response.data
    },

    // Features
    features: {
      /**
       * List enabled features
       * GET /v1/credits/features?organization_id=<uuid>
       */
      async list(organizationId: string): Promise<FeatureEntitlementListResponse> {
        const response = await axiosInstance.get('/v1/credits/features', {
          params: { organization_id: organizationId },
        })
        return response.data
      },

      /**
       * Get feature status
       * GET /v1/credits/features/:featureCode?organization_id=<uuid>
       */
      async getStatus(
        organizationId: string,
        featureCode: FeatureCode
      ): Promise<FeatureStatusResponse> {
        const response = await axiosInstance.get(`/v1/credits/features/${featureCode}`, {
          params: { organization_id: organizationId },
        })
        return response.data
      },

      /**
       * Enable a feature
       * POST /v1/credits/features/:featureCode/enable?organization_id=<uuid>
       */
      async enable(
        organizationId: string,
        featureCode: FeatureCode
      ): Promise<{ entitlement: FeatureEntitlement }> {
        const response = await axiosInstance.post(
          `/v1/credits/features/${featureCode}/enable`,
          {},
          { params: { organization_id: organizationId } }
        )
        return response.data
      },

      /**
       * Disable a feature
       * POST /v1/credits/features/:featureCode/disable?organization_id=<uuid>
       */
      async disable(
        organizationId: string,
        featureCode: FeatureCode
      ): Promise<FeatureDisableResponse> {
        const response = await axiosInstance.post(
          `/v1/credits/features/${featureCode}/disable`,
          {},
          { params: { organization_id: organizationId } }
        )
        return response.data
      },
    },

    // Usage
    usage: {
      /**
       * Get usage summary
       * GET /v1/credits/usage?organization_id=<uuid>&period=current_month
       */
      async getSummary(
        organizationId: string,
        options: {
          period?: 'current_month' | 'last_month' | 'last_7_days' | 'last_30_days' | 'custom'
          startDate?: string
          endDate?: string
        } = {}
      ): Promise<UsageSummaryResponseCredits> {
        const { startDate, endDate, ...rest } = options
        const response = await axiosInstance.get('/v1/credits/usage', {
          params: {
            organization_id: organizationId,
            start_date: startDate,
            end_date: endDate,
            ...rest,
          },
        })
        return response.data
      },

      /**
       * Get usage breakdown
       * GET /v1/credits/usage/breakdown?organization_id=<uuid>&period=current_month&group_by=day
       */
      async getBreakdown(
        organizationId: string,
        options: {
          period?: 'current_month' | 'last_month' | 'last_7_days' | 'last_30_days'
          groupBy?: 'day' | 'week' | 'month'
        } = {}
      ): Promise<UsageBreakdownResponse> {
        const { groupBy, ...rest } = options
        const response = await axiosInstance.get('/v1/credits/usage/breakdown', {
          params: {
            organization_id: organizationId,
            group_by: groupBy,
            ...rest,
          },
        })
        return response.data
      },
    },

    // Cost Center Credits
    costCenters: {
      /**
       * Get cost center credits
       * GET /v1/cost-centers/:costCenterId/credits
       */
      async getCredits(costCenterId: string): Promise<CostCenterCreditsResponse> {
        const response = await axiosInstance.get(`/v1/cost-centers/${costCenterId}/credits`)
        return response.data
      },

      /**
       * Allocate credits to cost center
       * POST /v1/cost-centers/:costCenterId/credits/allocate
       */
      async allocate(
        costCenterId: string,
        data: AllocateCreditsRequest
      ): Promise<AllocateCreditsResponse> {
        const response = await axiosInstance.post(
          `/v1/cost-centers/${costCenterId}/credits/allocate`,
          data
        )
        return response.data
      },

      /**
       * Deallocate credits from cost center
       * POST /v1/cost-centers/:costCenterId/credits/deallocate
       */
      async deallocate(
        costCenterId: string,
        data: DeallocateCreditsRequest
      ): Promise<DeallocateCreditsResponse> {
        const response = await axiosInstance.post(
          `/v1/cost-centers/${costCenterId}/credits/deallocate`,
          data
        )
        return response.data
      },
    },
  },

  // ===== Billing API =====
  billing: {
    /**
     * Get billing summary for organization
     * GET /v1/organizations/:organizationId/billing/summary
     */
    async getSummary(
      organizationId: string,
      options: { startDate?: string; endDate?: string } = {}
    ): Promise<BillingSummary> {
      const { startDate, endDate } = options
      const response = await axiosInstance.get(
        `/v1/organizations/${organizationId}/billing/summary`,
        { params: { start_date: startDate, end_date: endDate } }
      )
      return response.data
    },

    /**
     * Get billing breakdown by dimension
     * GET /v1/organizations/:organizationId/billing/breakdown
     */
    async getBreakdown(
      organizationId: string,
      options: {
        startDate?: string
        endDate?: string
        groupBy?: 'application' | 'costCenter' | 'workspace' | 'day' | 'week' | 'month'
        application?: string
        costCenterId?: string
      } = {}
    ): Promise<BillingBreakdown> {
      const { startDate, endDate, groupBy, costCenterId, ...rest } = options
      const response = await axiosInstance.get(
        `/v1/organizations/${organizationId}/billing/breakdown`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            group_by: groupBy,
            cost_center_id: costCenterId,
            ...rest,
          },
        }
      )
      return response.data
    },

    /**
     * Get billing trends over time
     * GET /v1/organizations/:organizationId/billing/trends
     */
    async getTrends(
      organizationId: string,
      options: {
        startDate?: string
        endDate?: string
        granularity?: 'day' | 'week' | 'month'
        application?: string
        costCenterId?: string
      } = {}
    ): Promise<BillingTrends> {
      const { startDate, endDate, costCenterId, ...rest } = options
      const response = await axiosInstance.get(
        `/v1/organizations/${organizationId}/billing/trends`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            cost_center_id: costCenterId,
            ...rest,
          },
        }
      )
      return response.data
    },

    /**
     * Export billing data
     * GET /v1/organizations/:organizationId/billing/export
     */
    async exportData(
      organizationId: string,
      options: {
        startDate: string
        endDate: string
        format?: 'csv' | 'json' | 'excel'
        groupBy?: 'costCenter' | 'workspace' | 'application' | 'day'
      }
    ): Promise<BillingExport | Blob> {
      const format = options.format || 'json'
      const { startDate, endDate, groupBy, ...rest } = options
      const response = await axiosInstance.get(
        `/v1/organizations/${organizationId}/billing/export`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            group_by: groupBy,
            ...rest,
          },
          responseType: format === 'json' ? 'json' : 'blob',
        }
      )
      return response.data
    },
  },

  // ===== Guardrails =====
  guardrails: {
    /**
     * GET /v1/workspaces/:workspaceId/guardrails/violations
     * List guardrail violations for a workspace
     */
    async listViolations(
      workspaceId: string,
      params?: { limit?: number; offset?: number; policyType?: string }
    ): Promise<GuardrailViolation[]> {
      const response = await axiosInstance.get<ListGuardrailViolationsResponse>(
        `/v1/workspaces/${workspaceId}/guardrails/violations`,
        { params }
      )
      return response.data.violations
    },

    /**
     * GET /v1/workspaces/:workspaceId/guardrails/violations/stats
     * Get violation statistics
     */
    async getViolationStats(
      workspaceId: string,
      params?: { days?: number }
    ): Promise<ViolationStats> {
      const response = await axiosInstance.get<ViolationStats>(
        `/v1/workspaces/${workspaceId}/guardrails/violations/stats`,
        { params }
      )
      return response.data
    },

    /**
     * GET /v1/workspaces/:workspaceId/gateway-configs/:configId/guardrails
     * Get guardrails configuration for a gateway config
     */
    async getConfig(workspaceId: string, configId: string): Promise<GuardrailsConfig> {
      const response = await axiosInstance.get<GuardrailsConfig>(
        `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/guardrails`
      )
      return response.data
    },

    /**
     * PUT /v1/workspaces/:workspaceId/gateway-configs/:configId/guardrails
     * Update guardrails configuration
     */
    async updateConfig(workspaceId: string, configId: string, config: GuardrailsConfig): Promise<GuardrailsConfig> {
      const response = await axiosInstance.put<GuardrailsConfig>(
        `/v1/workspaces/${workspaceId}/gateway-configs/${configId}/guardrails`,
        config
      )
      return response.data
    },
  },

  // ===== Notifications =====
  notifications: {
    /**
     * List notifications for the authenticated user
     * GET /v1/notifications
     */
    async list(params?: { isRead?: boolean; limit?: number }): Promise<{ notifications: UserNotification[]; unreadCount: number }> {
      const queryParams = new URLSearchParams()
      if (params?.isRead !== undefined) queryParams.set('is_read', String(params.isRead))
      if (params?.limit) queryParams.set('limit', String(params.limit))
      const response = await axiosInstance.get(`/v1/notifications?${queryParams.toString()}`)
      return response.data
    },

    /**
     * Get unread notification count
     * GET /v1/notifications/unread-count
     */
    async getUnreadCount(): Promise<{ unreadCount: number }> {
      const response = await axiosInstance.get('/v1/notifications/unread-count')
      return response.data
    },

    /**
     * Mark a notification as read or unread
     * PATCH /v1/notifications/:notificationId
     */
    async markRead(notificationId: string, isRead: boolean): Promise<{ notification: UserNotification }> {
      const response = await axiosInstance.patch(`/v1/notifications/${notificationId}`, { isRead })
      return response.data
    },

    /**
     * Mark all notifications as read
     * POST /v1/notifications/mark-all-read
     */
    async markAllRead(): Promise<{ updatedCount: number }> {
      const response = await axiosInstance.post('/v1/notifications/mark-all-read')
      return response.data
    },
  },

  // ===== RBAC =====
  rbac: {
    async getContext(): Promise<RbacContext> {
      const response = await axiosInstance.get('/v1/rbac/context')
      return response.data
    },
  },
}

export interface RbacContext {
  userId: string
  platformRole: string
  isSuperAdmin: boolean
  isPlatformAdmin: boolean
  organizations: {
    organizationId: string
    organizationName: string
    organizationSlug: string
    role: string
  }[]
  workspaces: unknown[]
  teams: unknown[]
  deploymentMode: string
  simplifiedExperience: boolean
  canManageInfrastructure: boolean
  canManageUsers: boolean
  needsOnboarding: boolean
  creditsEnabled: boolean
}

// ===== Notification Types =====
export interface UserNotification {
  id: string
  userId: string
  title: string
  message: string
  notificationType: 'info' | 'success' | 'warning' | 'error'
  isRead: boolean
  resourceType?: string
  resourceId?: string
  createdAt: string
  updatedAt: string
}

// Export error class for error handling
export { BackendApiError }
